import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Button, Modal, Spinner } from 'flowbite-react'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { useUserInfo } from '../../react-query/queries'
import { GoogleGenAI, createPartFromUri } from '@google/genai'
import { useQueryClient } from '@tanstack/react-query'
import { getCourseChapterOptions, getCourseChapters, request } from '../../utils/remote'
import ProgressBar from '../../components/ProgressBar'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import DOMPurify from 'dompurify'

interface MetaProps {
    questions: any[]
    onSave: (questionsWithMeta: any[], options?: { partial?: boolean }) => void
    onBack?: () => void
    subjectId?: string
    subjectName?: string
    gradeId?: string
    examId?: string | null
    onRegisterSave?: (fn: () => void) => void
}

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

// Render LaTeX expressions inside an HTML string using KaTeX.
const renderLatexInHtml = (html: string) => {
    if (!html) return ''
    try {
        let out = String(html)
        out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => {
            try {
                return katex.renderToString(expr, { displayMode: true, throwOnError: false })
            } catch (e) {
                return _m
            }
        })
        out = out.replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => {
            try {
                return katex.renderToString(expr, { displayMode: false, throwOnError: false })
            } catch (e) {
                return _m
            }
        })
        try {
            return DOMPurify.sanitize(out)
        } catch (e) {
            return out
        }
    } catch (e) {
        try { return DOMPurify.sanitize(String(html)) } catch (err) { return String(html) }
    }
}

interface QuestionItemProps {
    q: any;
    index: number;
    colorIndex: number;
    targetLevel: any;
    runningSingleRag: number | null;
    runRagForSingleQuestion: (idx: number) => void;
    setSingleChapterIndex: (idx: number | null) => void;
    setShowChapterModal: (show: boolean) => void;
    isRunningChapterAssign: boolean;
    chapterAlone: boolean;
    useChapterAndSubchapter: boolean;
    subToParent: Record<string, number>;
    updateQuestionAt: (index: number, patch: any) => void;
    setField: (index: number, field: string, value: any) => void;
    combinedChapters: any[];
    subchapterCache: Record<number, any[]>;
    fetchSubchaptersForId: (id: number) => Promise<void>;
    mapToApiChapterId: (candidate: any) => number | null;
    mapToSubjectChapterId: (candidate: any) => number | null;
    token: string | null;
    showToast: (msg: string, type?: 'success' | 'error') => void;
}

const QuestionItem = React.memo(({
    q,
    index,
    colorIndex,
    targetLevel,
    runningSingleRag,
    runRagForSingleQuestion,
    setSingleChapterIndex,
    setShowChapterModal,
    isRunningChapterAssign,
    chapterAlone,
    useChapterAndSubchapter,
    subToParent,
    updateQuestionAt,
    setField,
    combinedChapters,
    subchapterCache,
    fetchSubchaptersForId,
    mapToApiChapterId,
    mapToSubjectChapterId,
    token,
    showToast
}: QuestionItemProps) => {
    
    // Memoize heavy rendering
    const renderedQuestion = useMemo(() => renderLatexInHtml(q.question), [q.question]);
    const renderedExplanation = useMemo(() => q.explanation ? renderLatexInHtml(String(q.explanation)) : null, [q.explanation]);

    // Derived values
    const ansRaw = (q.answer ?? q.ans ?? q.correct_ans ?? '').toString().trim()
    let label = ansRaw.toUpperCase()
    if (/^[1-4]$/.test(label)) {
        const idx = Number(label) - 1
        label = String.fromCharCode(65 + idx)
    }
    const choiceText = useMemo(() => {
        try {
            if (Array.isArray(q.choices) && q.choices.length) {
                const idx = label.charCodeAt(0) - 65
                if (idx >= 0 && idx < q.choices.length) return String(q.choices[idx])
            }
        } catch (e) {}
        const map: Record<string,string|undefined> = {
            'A': q.choice_a ?? q.option1 ?? q.option_1 ?? q.optionA ?? undefined,
            'B': q.choice_b ?? q.option2 ?? q.option_2 ?? q.optionB ?? undefined,
            'C': q.choice_c ?? q.option3 ?? q.option_3 ?? q.optionC ?? undefined,
            'D': q.choice_d ?? q.option4 ?? q.option_4 ?? q.optionD ?? undefined,
        }
        return map[label] ?? ''
    }, [q, label]);

    const renderedChoiceText = useMemo(() => choiceText ? renderLatexInHtml(String(choiceText)) : null, [choiceText]);
    const geminiExplanation = q.ragAssignment?.geminiSuggestion?.explanation;
    const renderedGeminiExplanation = useMemo(() => geminiExplanation ? geminiExplanation : null, [geminiExplanation]);

    return (
        <div className="relative flex flex-col rounded-lg border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark">
            {/* Left colored border */}
            <div className={`absolute left-0 top-0 h-full w-1.5 rounded-l-lg ${colorIndex % 4 === 0 ? 'bg-blue-500' : colorIndex % 4 === 1 ? 'bg-pink-500' : colorIndex % 4 === 2 ? 'bg-orange-500' : 'bg-yellow-500'}`}></div>

            {/* Header section */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-semibold">Q{index + 1}</div>
                    <div>
                        <h3 className="font-semibold text-black dark:text-white">Question {index + 1}</h3>
                        <p className="text-sm text-bodydark2">ID: {q.id || 'New'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="xs" color="light" onClick={() => runRagForSingleQuestion(index)} disabled={runningSingleRag === index}>
                        {runningSingleRag === index && <Spinner size="sm" className="mr-2" />}
                        Get suggestion for this question
                    </Button>
                    {targetLevel === 'college_university' && (
                        <Button size="xs" color="light" onClick={() => { setSingleChapterIndex(index); setShowChapterModal(true); }} disabled={isRunningChapterAssign}>
                            Get chapter for this question
                        </Button>
                    )}
                    {q.ragAssignment && q.ragAssignment.status === 'done' && q.ragAssignment.title ? (
                        <span className="inline-flex rounded-full bg-primary bg-opacity-10 px-3 py-1 text-sm font-medium text-primary">
                            {q.ragAssignment.title}
                        </span>
                    ) : q.ragAssignment && q.ragAssignment.status === 'running' ? (
                        <span className="inline-flex rounded-full bg-meta-3 bg-opacity-10 px-3 py-1 text-sm font-medium text-meta-3">
                            Running RAG
                        </span>
                    ) : q.ragAssignment && q.ragAssignment.status === 'failed' ? (
                        <span className="inline-flex rounded-full bg-red-500 bg-opacity-10 px-3 py-1 text-sm font-medium text-red-500">
                            RAG Failed
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Question content */}
            <div className="mb-4" dangerouslySetInnerHTML={{ __html: renderedQuestion }} />

            {/* Answer choice */}
            {ansRaw && (
                <div className="mb-3">
                    <div className="text-sm text-bodydark2 font-medium">Answer: <span className="font-semibold">{label}</span></div>
                    {renderedChoiceText && (
                        <div className="mt-1 text-sm p-2 bg-gray-50 border rounded" dangerouslySetInnerHTML={{ __html: renderedChoiceText }} />
                    )}
                </div>
            )}

            {/* RAG suggestion details and actions */}
            {q.ragAssignment ? (
                <div className="mb-3 p-3 border rounded bg-gray-50 dark:bg-meta-4 space-y-3">
                    {q.ragAssignment.status === 'running' && <div className="text-sm text-bodydark2 flex items-center gap-2"><Spinner size="sm" /> Running...</div>}
                    {q.ragAssignment.status === 'failed' && <div className="text-sm text-red-600">Failed: {q.ragAssignment.error || 'RAG failed'}</div>}

                    {q.ragAssignment.geminiSuggestion ? (
                        <div className="">
                            <div className="font-medium">Suggested chapter: {q.ragAssignment.geminiSuggestion.chapter_name || q.ragAssignment.title || 'Unknown'}</div>
                            <div className="text-sm text-bodydark2">Chapter id: {String(q.ragAssignment.geminiSuggestion.chapter_id ?? q.ragAssignment.textbookChapterId ?? q.ragAssignment.apiChapterId ?? 'unknown')}</div>
                            {q.ragAssignment.assigned_chaporsubid != null ? (
                                <div className="text-sm text-bodydark2">Assigned chap/sub id: {String(q.ragAssignment.assigned_chaporsubid)} {q.ragAssignment.assigned_name ? `— ${q.ragAssignment.assigned_name}` : ''}</div>
                            ) : null}

                            <div className="mt-2">
                                <div className="font-medium">Suggested explanation</div>
                                <div className="text-sm text-bodydark2 mt-1">
                                    {renderedGeminiExplanation && (
                                        <div className="mb-2 p-2 bg-white border rounded" dangerouslySetInnerHTML={{ __html: String(renderedGeminiExplanation) }} />
                                    )}
                                </div>
                            </div>

                            {q.ragAssignment.geminiSuggestion.corrected_answer ? (
                                <div className="mt-2">
                                    <div className="font-medium">Gemini corrected answer</div>
                                    <div className="text-sm text-red-600">{q.ragAssignment.geminiSuggestion.corrected_answer}</div>
                                </div>
                            ) : null}

                            {Array.isArray(q.ragAssignment.geminiSuggestion.references) && q.ragAssignment.geminiSuggestion.references.length > 0 && (
                                <div className="mt-2">
                                    <div className="font-medium">References</div>
                                    <ul className="text-sm text-bodydark2 list-disc pl-5 mt-1">
                                        {q.ragAssignment.geminiSuggestion.references.map((r: string, ri: number) => (
                                            <li key={ri} className="mb-1">
                                                <div className="text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(r)) }} />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex items-center gap-2 mt-3">
                                {((!chapterAlone) || (q.ragAssignment && q.ragAssignment.assigned_chaporsubid != null)) && (
                                    <button className={`btn btn-sm px-2 py-1 rounded ${q.ragAssignment.acceptedChapter ? 'bg-green-500 text-white' : 'bg-gray-200'}`} onClick={async () => {
                                        const accepted = !q.ragAssignment.acceptedChapter
                                        const assignedId = q.ragAssignment?.assigned_chaporsubid ?? null
                                        const assignedIsSub = typeof q.ragAssignment?.assigned_is_subchapter === 'boolean' ? q.ragAssignment.assigned_is_subchapter : null

                                        try {
                                            if (useChapterAndSubchapter && assignedId != null) {
                                                if (assignedIsSub) {
                                                    const parent = subToParent[String(assignedId)] || null
                                                    if (parent) {
                                                        updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedChapter: accepted, accepted: accepted }, chapter: String(parent) })
                                                        setField(index, 'chapter', String(parent))
                                                    }
                                                    updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedChapter: accepted, accepted: accepted }, subchapter: String(assignedId) })
                                                    setField(index, 'subchapter', String(assignedId))
                                                } else {
                                                    updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedChapter: accepted, accepted: accepted }, chapter: String(assignedId) })
                                                    setField(index, 'chapter', String(assignedId))
                                                    setField(index, 'subchapter', '')
                                                }
                                            } else {
                                                const gemChapter = q.ragAssignment?.geminiSuggestion?.chapter_id
                                                const textbookCandidate = q.ragAssignment.textbookChapterId ?? null
                                                const apiCandidate = q.ragAssignment.apiChapterId ?? null
                                                const chosen = gemChapter != null ? gemChapter : (apiCandidate != null ? apiCandidate : textbookCandidate)
                                                const apiToSend = mapToApiChapterId(chosen) || chosen
                                                const mappedSubjectId = mapToSubjectChapterId(apiToSend) || null
                                                const chapterSubjectToSend = mappedSubjectId != null ? mappedSubjectId : (apiToSend ?? '')
                                                updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedChapter: accepted, accepted: accepted }, chapter: String(chapterSubjectToSend) })
                                                setField(index, 'chapter', String(chapterSubjectToSend))
                                            }
                                        } catch (e) { }

                                        if (accepted && q.id) {
                                            try {
                                                updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, applyingChapter: true } })
                                                let body: any
                                                if (useChapterAndSubchapter) {
                                                    body = { question: q.id, is_chapteroption: true }
                                                    if (assignedId != null) {
                                                        if (assignedIsSub) {
                                                            body.subchapter_option = Number(assignedId)
                                                            const parent = subToParent[String(assignedId)] || null
                                                            if (parent) body.chapter_option = Number(parent)
                                                        } else {
                                                            body.chapter_option = Number(assignedId)
                                                        }
                                                    } else {
                                                        if (q.subchapter && String(q.subchapter).trim()) {
                                                            body.subchapter_option = Number(q.subchapter)
                                                            const parent = subToParent[String(q.subchapter)] || null
                                                            if (parent) body.chapter_option = Number(parent)
                                                            else if (q.chapter && String(q.chapter).trim()) body.chapter_option = Number(q.chapter)
                                                        } else if (q.chapter && String(q.chapter).trim()) {
                                                            body.chapter_option = Number(q.chapter)
                                                        }
                                                    }
                                                } else {
                                                    if (assignedId != null) {
                                                        body = { question: q.id, chapter: Number(assignedId) }
                                                    } else {
                                                        const gemChapter = q.ragAssignment?.geminiSuggestion?.chapter_id
                                                        const textbookCandidate = q.ragAssignment.textbookChapterId ?? null
                                                        const apiCandidate = q.ragAssignment.apiChapterId ?? null
                                                        const chosen = gemChapter != null ? gemChapter : (apiCandidate != null ? apiCandidate : textbookCandidate)
                                                        const apiToSend = mapToApiChapterId(chosen) || chosen
                                                        const mappedSubjectId = mapToSubjectChapterId(apiToSend) || null
                                                        const chapterSubjectToSend = mappedSubjectId != null ? mappedSubjectId : (apiToSend ?? '')
                                                        body = { question: q.id, chapter: (chapterSubjectToSend !== '' ? Number(chapterSubjectToSend) : chapterSubjectToSend) }
                                                    }
                                                }

                                                await request(`/api/school/exam-detail/${q.exam || ''}/add-change-chapter/`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }, body: JSON.stringify(body) })
                                                updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, appliedChapter: true, applyingChapter: false } })
                                                showToast('Chapter applied', 'success')
                                            } catch (err) {
                                                console.error('Apply chapter failed', err)
                                                updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, applyingChapter: false, chapterApplyError: String(err) } })
                                                showToast(String(err) || 'Apply chapter failed', 'error')
                                            }
                                        }
                                    }}>{q.ragAssignment.acceptedChapter ? 'Chapter accepted' : 'Accept chapter'}</button>
                                )}

                                <button className={`btn btn-sm px-2 py-1 rounded ${q.ragAssignment.acceptedChapter === false ? 'bg-red-500 text-white' : 'bg-gray-200'}`} onClick={() => {
                                    updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedChapter: false } })
                                }}>Reject chapter</button>

                                <button className={`btn btn-sm px-2 py-1 rounded ${q.ragAssignment.acceptedExplanation ? 'bg-green-500 text-white' : 'bg-gray-200'}`} onClick={async () => {
                                    const accepted = !q.ragAssignment.acceptedExplanation
                                    const explanationHtml = q.ragAssignment.geminiSuggestion.explanation || ''
                                    const corrected = q.ragAssignment?.geminiSuggestion?.corrected_answer

                                    try {
                                        updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedExplanation: accepted, accepted: accepted }, explanation: explanationHtml })
                                        setField(index, 'explanation', explanationHtml)
                                        if (corrected && String(corrected).trim()) {
                                            setField(index, 'corrected_answer', String(corrected))
                                        }
                                    } catch (e) { }

                                    if (accepted && q.id) {
                                        try {
                                            updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, applyingExplanation: true } })
                                            const body: any = { question: q.id, explanation: explanationHtml }
                                            if (corrected && String(corrected).trim()) body.corrected_answer = String(corrected)
                                            await request(`/api/school/exam-detail/${q.exam || ''}/add-change-chapter/`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }, body: JSON.stringify(body) })
                                            updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, appliedExplanation: true, applyingExplanation: false } })
                                        } catch (err) {
                                            console.error('Apply explanation failed', err)
                                            updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, applyingExplanation: false, explanationApplyError: String(err) } })
                                        }
                                    }
                                }}>{q.ragAssignment.acceptedExplanation ? 'Explanation accepted' : 'Accept explanation'}</button>

                                <button className="btn btn-sm bg-red-500 text-white px-2 py-1 rounded" onClick={() => {
                                    updateQuestionAt(index, { ragAssignment: { ...q.ragAssignment, acceptedExplanation: false } })
                                }}>Reject explanation</button>
                            </div>
                        </div>
                    ) : (
                        <div className="font-medium">{q.ragAssignment.title || q.ragAssignment.label || 'No suggestion'}</div>
                    )}
                </div>
            ) : (
                <div className="text-sm text-bodydark2">No RAG suggestion run yet.</div>
            )}

            <label htmlFor={`chapter-${index}`} className="block mb-1">Chapter</label>
            {combinedChapters && combinedChapters.length > 0 ? (
                <select id={`chapter-${index}`} aria-label={`chapter for question ${index + 1}`} value={q.chapter || ''} onChange={async (e) => {
                    const val = e.target.value
                    setField(index, 'chapter', val)
                    setField(index, 'subchapter', '')
                    try {
                        const num = Number(val)
                        if (!Number.isNaN(num) && num) await fetchSubchaptersForId(num)
                    } catch (err) { }
                    try {
                        const subj = mapToSubjectChapterId(val)
                        if (subj != null && subj !== Number(val)) await fetchSubchaptersForId(subj)
                    } catch (err) { }
                }} className="w-full rounded border p-2 mb-3">
                    <option value="">-- Select chapter --</option>
                    {combinedChapters.map((ch: any) => {
                        const valueToUse = ch.api_chapter_id != null ? ch.api_chapter_id : ch.id
                        return (<option key={(ch.api_chapter_id != null ? `api-${ch.api_chapter_id}` : `id-${ch.id}`)} value={valueToUse}>{ch.name || ch.title || `Chapter ${ch.id}`}</option>)
                    })}
                </select>
            ) : (
                <input id={`chapter-${index}`} aria-label={`chapter for question ${index + 1}`} placeholder="e.g. Algebra - Quadratic equations" value={q.chapter} onChange={(e) => setField(index, 'chapter', e.target.value)} className="w-full rounded border p-2 mb-3" />
            )}

            {/* Subchapter select */}
            {(() => {
                const chapVal = q.chapter
                const chapId = chapVal ? Number(chapVal) : null
                const subs = chapId ? (subchapterCache[chapId] || []) : []
                if (subs && subs.length > 0) {
                    return (
                        <>
                            <label htmlFor={`subchapter-${index}`} className="block mb-1">Subchapter</label>
                            <select id={`subchapter-${index}`} aria-label={`subchapter for question ${index + 1}`} value={q.subchapter ?? ''} onChange={(e) => {
                                const val = e.target.value
                                setField(index, 'subchapter', val)
                                try {
                                    const parent = subToParent[String(val)]
                                    if (parent) setField(index, 'chapter', String(parent))
                                } catch (err) { }
                            }} className="w-full rounded border p-2 mb-3">
                                <option value="">-- Select subchapter --</option>
                                {subs.map((s: any) => (<option key={s.id} value={s.id}>{s.name || s.title || `Subchapter ${s.id}`}</option>))}
                            </select>
                        </>
                    )
                }
                return null
            })()}

            <label className="block mb-1">Explanation</label>
            {renderedExplanation && (
                <div className="mb-2 p-2 bg-white border rounded" dangerouslySetInnerHTML={{ __html: renderedExplanation }} />
            )}
        </div>
    )
})

const ExamQuestionsMeta: React.FC<MetaProps> = ({ questions, onSave, onBack: _onBack, subjectId, subjectName, gradeId, examId, onRegisterSave }) => {
    const [localQuestions, setLocalQuestions] = useState(() => questions.map(q => ({ ...q, chapter: q.chapter || '', explanation: q.explanation || '', ragAssignment: null })))
    const localQuestionsRef = useRef(localQuestions)
    useEffect(() => { localQuestionsRef.current = localQuestions }, [localQuestions])

    const [isSaving, setIsSaving] = useState(false)
    const { token, role } = useAuth()
    const { data: userData } = useUserInfo()

    const targetLevel: 'school' | 'college_university' | undefined = ((): any => {
        const raw = (userData?.institution_type ?? userData?.institution_name ?? '') as string
        const instType = String(raw || '').trim().toLowerCase()
        if (instType.includes('school')) return 'school'
        if (instType.includes('college') || instType.includes('university') || instType.includes('college_university')) return 'college_university'
        const r = String(role ?? '').toLowerCase()
        if (r === 'school') return 'school'
        if (r.includes('college') || r.includes('university') || r === 'college_university') return 'college_university'
        return undefined
    })()
    const queryClient = useQueryClient()
    const [subchapterCache, setSubchapterCache] = useState<Record<number, any[]>>({})
    const [subToParent, setSubToParent] = useState<Record<string, number>>({})
    const [useChapterAndSubchapter, setUseChapterAndSubchapter] = useState<boolean>(true)
    
    // chapters available for the exam's subject/grade (used to populate the chapter dropdown)
    const [subjectChapters, setSubjectChapters] = useState<any[]>([])
    // chapters available for the currently selected RAG/book (used for textbook->api mapping during RAG runs)
    const [bookChapters, setBookChapters] = useState<any[] | null>(null)
    // per-book chapter lists keyed by book id (keeps chapters separate per selected RAG)
    const [perBookChaptersMap, setPerBookChaptersMap] = useState<Record<string, any[]>>({})
    // course-specific chapter options fetched during chapter-assign runs (makes option-mode chapters visible)
    const [courseChapterOptionsMap, setCourseChapterOptionsMap] = useState<Record<string, any[]>>({})

    const fetchSubchaptersForId = useCallback(async (chapterId: number) => {
        if (!chapterId) return
        try {
            if (useChapterAndSubchapter) {
                try {
                    const raw = localStorage.getItem('add_exam_subjects')
                    let courseIds: string[] = []
                    if (raw) {
                        try { courseIds = JSON.parse(raw) as string[] } catch { courseIds = (raw || '').split(',').map(s => s.trim()).filter(Boolean) }
                    }
                    for (const cid of courseIds) {
                        try {
                            const cdata = await queryClient.fetchQuery<any>({ queryKey: ['course_chapter_options', cid], queryFn: () => getCourseChapterOptions(token ?? null, Number(cid)), staleTime: 5 * 60 * 1000 })
                            const chs = (cdata && cdata.chapters) ? cdata.chapters : []
                            for (const ch of chs) {
                                if (Number(ch.id) === Number(chapterId)) {
                                    const subs = Array.isArray(ch.subchapter_options) && ch.subchapter_options.length ? ch.subchapter_options : (Array.isArray(ch.subchapters) ? ch.subchapters : [])
                                    setSubchapterCache(prev => ({ ...prev, [chapterId]: subs || [] }))
                                    const map: Record<string, number> = {}
                                    for (const sc of (subs || [])) { try { map[String(sc.id)] = Number(ch.id) } catch (e) {} }
                                    if (Object.keys(map).length) setSubToParent(prev => ({ ...prev, ...map }))
                                    return
                                }
                            }
                        } catch (e) { continue }
                    }
                } catch (e) { }
                setSubchapterCache(prev => ({ ...prev, [chapterId]: [] }))
                return
            }

            const subj = (subjectChapters || []).find((c: any) => Number(c.id) === Number(chapterId))
            if (subj) {
                const subs = Array.isArray(subj.subchapters) && subj.subchapters.length ? subj.subchapters : (Array.isArray(subj.course_subchapters) ? subj.course_subchapters : [])
                setSubchapterCache(prev => ({ ...prev, [chapterId]: subs || [] }))
                const map: Record<string, number> = {}
                for (const sc of (subs || [])) { try { map[String(sc.id)] = Number(subj.id) } catch (e) {} }
                if (Object.keys(map).length) setSubToParent(prev => ({ ...prev, ...map }))
                return
            }

            const allBookLists = [ ...(Object.values(perBookChaptersMap || {})).flat(), ...(bookChapters || []) ]
            for (const bch of allBookLists) {
                if (!bch) continue
                if (Number(bch.id) === Number(chapterId)) {
                    const subs = Array.isArray(bch.subchapters) ? bch.subchapters : []
                    setSubchapterCache(prev => ({ ...prev, [chapterId]: subs || [] }))
                    const map: Record<string, number> = {}
                    for (const sc of (subs || [])) { try { map[String(sc.id)] = Number(bch.id) } catch (e) {} }
                    if (Object.keys(map).length) setSubToParent(prev => ({ ...prev, ...map }))
                    return
                }
            }

            setSubchapterCache(prev => ({ ...prev, [chapterId]: [] }))
        } catch (e) {
            setSubchapterCache(prev => ({ ...prev, [chapterId]: [] }))
        }
    }, [useChapterAndSubchapter, token, subjectChapters, perBookChaptersMap, bookChapters]) 

    const PAGE_SIZE = 10
    const [page, setPage] = useState<number>(0)

    useEffect(() => {
        const start = page * PAGE_SIZE
        const end = Math.min(start + PAGE_SIZE, localQuestions.length)
        const needed = new Set<number>()
        for (let i = start; i < end; i++) {
            const q = localQuestions[i]
            const c = q && q.chapter ? Number(q.chapter) : null
            if (c && !Object.prototype.hasOwnProperty.call(subchapterCache, c)) needed.add(c)
        }
        if (needed.size === 0) return
        for (const cid of Array.from(needed)) fetchSubchaptersForId(cid)
    }, [localQuestions, subchapterCache, fetchSubchaptersForId, page, PAGE_SIZE])
    
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null)
    const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type })
        window.setTimeout(() => setToast(null), 3500)
    }, [])

    const [showRagModal, setShowRagModal] = useState(false)
    const [rags, setRags] = useState<any[] | null>(null)
    const [allRags, setAllRags] = useState<any[] | null>(null)
    const [selectedRags, setSelectedRags] = useState<string[]>([])
    const [isLoadingRags, setIsLoadingRags] = useState(false)
    const [isRunningRag, setIsRunningRag] = useState(false)
    const [showChapterModal, setShowChapterModal] = useState(false)
    const [chapterBatchSize, setChapterBatchSize] = useState<number>(5)
    const [isRunningChapterAssign, setIsRunningChapterAssign] = useState(false)
    const [geminiKey, setGeminiKey] = useState<string | null>(null)
    const [isCourseIdsRequest, setIsCourseIdsRequest] = useState<boolean>(false)
    const [singleChapterIndex, setSingleChapterIndex] = useState<number | null>(null)
    const [geminiBatchSize, setGeminiBatchSize] = useState<number>(5)
    const [ragProgress, setRagProgress] = useState<{ total: number, completed: number }>({ total: 0, completed: 0 })
    const [runningSingleRag, setRunningSingleRag] = useState<number | null>(null)
    const [chapterAlone, setChapterAlone] = useState<boolean>(false)

    const hasCompletedRagSuggestions = useMemo(() => {
        if (ragProgress.total === 0 || ragProgress.completed !== ragProgress.total) return false;
        return localQuestions.some(q => q.ragAssignment?.geminiSuggestion);
    }, [ragProgress, localQuestions]);

    const setField = useCallback((index: number, field: string, value: any) => {
        setLocalQuestions(prev => {
            const copy = [...prev]
            copy[index] = { ...copy[index], [field]: value }
            return copy
        })
    }, [])

    const updateQuestionAt = useCallback((index: number, patch: any) => {
        setLocalQuestions(prev => {
            const copy = [...prev]
            copy[index] = { ...copy[index], ...patch }
            return copy
        })
    }, [])

    const fetchRags = async () => {
        if (!questions || questions.length === 0) return
        setIsLoadingRags(true)
        try {
            let url = `/api/textbook/list/`;
            try {
                if (targetLevel === 'college_university') {
                    try {
                        const raw = localStorage.getItem('add_exam_subjects')
                        if (raw) {
                            const arr = JSON.parse(raw)
                            if (Array.isArray(arr) && arr.length) {
                                const qp = new URLSearchParams()
                                qp.set('course_ids', arr.join(','))
                                url = `/api/textbook/list/?${qp.toString()}`
                            }
                        }
                    } catch (e) { }
                }
            } catch (e) { }
            const isReq = url.includes('course_ids=')
            setIsCourseIdsRequest(isReq)
            const data = await request(url, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
            const books = data?.books || []
            console.log('DEBUG: fetchRags all books:', books)
            console.log('DEBUG: fetchRags params - subjectId:', subjectId, 'subjectName:', subjectName, 'gradeId:', gradeId)
            
            setAllRags(books)
            if (isReq) {
                setRags(books)
            } else {
                const gid = gradeId ? Number(gradeId) : null
                const sid = subjectId ? String(subjectId) : null
                const sname = subjectName ? subjectName.toLowerCase().trim() : null
                const filtered = books.filter((b: any) => {
                    try {
                        const gradeArr = Array.isArray(b.school_grade_ids) ? b.school_grade_ids : (Array.isArray(b.school_grades) ? b.school_grades : [])
                        const bookSubjectId = (b.school_subject_id != null) ? String(b.school_subject_id) : ((b.subject_id != null) ? String(b.subject_id) : null)
                        const bookSubjectName = b.subject ? String(b.subject).toLowerCase().trim() : null

                        const hasGradeMatch = (gid == null) ? false : (Array.isArray(gradeArr) && gradeArr.length ? gradeArr.map((x: any) => Number(x)).includes(gid) : false)
                        const hasSubjectMatch = (sid != null && bookSubjectId != null && String(bookSubjectId) === sid) || (sname != null && bookSubjectName != null && bookSubjectName === sname)

                        if (gid != null && (sid != null || sname != null)) {
                            return hasGradeMatch && hasSubjectMatch
                        }
                        if (gid != null) return hasGradeMatch
                        if (sid != null || sname != null) return hasSubjectMatch
                        return true
                    } catch (e) {
                        return false
                    }
                })
                setRags(filtered)
            }
        } catch (err) {
            console.error('Failed to load RAGs', err)
            setRags([])
        } finally {
            setIsLoadingRags(false)
        }
    }

    const fetchSubjectChapters = async () => {
        if (targetLevel === 'college_university') { setSubjectChapters([]); return }
        if (!subjectId || !gradeId) { setSubjectChapters([]); return }
        try {
            const qp = new URLSearchParams()
            qp.set('grade_id', String(gradeId))
            const url = `/api/school/subjects/${subjectId}/chapters/?${qp.toString()}`
            const data = await request(url, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
            const chapters = data?.chapters || []
            const gid = Number(gradeId)
            const filtered = chapters.filter((c: any) => {
                try {
                    if (!c) return false
                    const gradeArr = Array.isArray(c.school_grades) && c.school_grades.length ? c.school_grades : (Array.isArray(c.school_grade_ids) ? c.school_grade_ids : [])
                    if (!Array.isArray(gradeArr) || gradeArr.length === 0) return false
                    return gradeArr.map((x: any) => Number(x)).includes(gid)
                } catch (e) {
                    return false
                }
            })
            setSubjectChapters(filtered)
        } catch (err) {
            setSubjectChapters([])
        }
    }

    useEffect(() => {
        fetchSubjectChapters()
    }, [subjectId, gradeId, token, targetLevel])

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(localQuestions.length / PAGE_SIZE) - 1)
        setPage(prev => Math.min(prev, maxPage))
    }, [localQuestions.length])

    useEffect(() => {
        const loadKey = async () => {
            if (!token) return
            try {
                const data = await request('/api/user/gemini-key/', { method: 'GET', headers: { Authorization: token ? `Bearer ${token}` : '' } })
                if (!data) return
                if (data?.has_key && data?.api_key) setGeminiKey(String(data.api_key))
            } catch (e) { }
        }
        loadKey()
    }, [token])

    const combinedChapters = useMemo(() => {
        const byKey: Record<string, any> = {}
        const push = (c: any) => {
            if (!c) return
            const key = c.api_chapter_id != null ? `api:${c.api_chapter_id}` : (c.id != null ? `id:${c.id}` : JSON.stringify(c))
            if (!byKey[key]) byKey[key] = c
        }
        (subjectChapters || []).forEach(push)
        for (const arr of Object.values(perBookChaptersMap || {})) {
            (arr || []).forEach(push)
        }
        for (const arr of Object.values(courseChapterOptionsMap || {})) {
            (arr || []).forEach(push)
        }
        (bookChapters || []).forEach(push)
        return Object.values(byKey)
    }, [subjectChapters, perBookChaptersMap, bookChapters, courseChapterOptionsMap])

    const mapToSubjectChapterId = useCallback((candidate: any): number | null => {
        try {
            if (candidate == null || String(candidate).trim() === '') return null
            const sval = String(candidate)
            const direct = (subjectChapters || []).find((c: any) => String(c.id) === sval)
            if (direct) return Number(direct.id)
            const byApi = (subjectChapters || []).find((c: any) => c.api_chapter_id != null && String(c.api_chapter_id) === sval)
            if (byApi) return Number(byApi.id)
            const fromBook = (bookChapters || []).find((c: any) => String(c.id) === sval)
            if (fromBook && fromBook.api_chapter_id != null) {
                const mapped = (subjectChapters || []).find((c: any) => c.api_chapter_id != null && String(c.api_chapter_id) === String(fromBook.api_chapter_id))
                if (mapped) return Number(mapped.id)
            }
            return null
        } catch (e) {
            return null
        }
    }, [subjectChapters, bookChapters])

    const mapToApiChapterId = useCallback((candidate: any): number | null => {
        try {
            if (candidate == null || String(candidate).trim() === '') return null
            const sval = String(candidate)
            const byApiDirect = (subjectChapters || []).find((c: any) => c.api_chapter_id != null && String(c.api_chapter_id) === sval)
            if (byApiDirect) return Number(byApiDirect.api_chapter_id)
            const bySubject = (subjectChapters || []).find((c: any) => String(c.id) === sval && c.api_chapter_id != null)
            if (bySubject) return Number(bySubject.api_chapter_id)
            const fromBook = (bookChapters || []).find((c: any) => String(c.id) === sval && c.api_chapter_id != null)
            if (fromBook) return Number(fromBook.api_chapter_id)
            if (!Number.isNaN(Number(sval))) return Number(sval)
            return null
        } catch (e) {
            return null
        }
    }, [subjectChapters, bookChapters])

    const runRagForQuestions = async () => {
        if (!selectedRags || selectedRags.length === 0) return;
        setIsRunningRag(true);
        try {
            const perBookChapters: Record<string, any[]> = {};
            for (const bookId of selectedRags) {
                try {
                    const chaptersData = await request(`/api/textbook/${bookId}/chapters/`, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
                    perBookChapters[String(bookId)] = chaptersData.chapters || [];
                } catch (e) {
                    perBookChapters[String(bookId)] = [];
                }
            }
            const textbookToApiMap: Record<string, number | null> = {};
            Object.values(perBookChapters).forEach((chapterList: any[]) => {
                chapterList.forEach((c: any) => { textbookToApiMap[String(c.id)] = c.api_chapter_id || null });
            });
            setPerBookChaptersMap(perBookChapters);
            setBookChapters(Object.values(perBookChapters).flat());

            const start = page * PAGE_SIZE
            const end = Math.min(start + PAGE_SIZE, localQuestions.length)
            const pageIndices = [] as number[]
            for (let i = start; i < end; i++) pageIndices.push(i)
            setRagProgress({ total: pageIndices.length, completed: 0 });
            const pendingGemini: Array<any> = []
            const flushGeminiBatch = async () => {
                if (!geminiKey || pendingGemini.length === 0) return
                try {
                    const ai = new GoogleGenAI({ apiKey: geminiKey.trim() })

                    const imageItems = pendingGemini.filter((p: any) => p.hasImage)
                    const nonImageItems = pendingGemini.filter((p: any) => !p.hasImage)

                    if (nonImageItems.length > 0) {
                            const batchPayload = nonImageItems.map((p: any) => ({
                            question_idx: p.idx,
                            question: p.question,
                            choices: p.choices,
                            student_answer: p.answer,
                            hits: p.hitsPayload
                        }))
                        const courseNames = Array.from(new Set((allRags || []).flatMap((b: any) => (b && b.course_names) ? b.course_names : []))).filter(Boolean)
                        const coursePriorityInstruction = isCourseIdsRequest && courseNames.length ? `\nCourse prioritization: the following courses are in-scope: ${JSON.stringify(courseNames)}. For each question, first output a field \"determined_course\" with the chosen course name (one of the list) or null if none. When producing explanation and references, prioritize hits coming from book(s) associated with the determined_course.` : ''

                        const prompt = chapterAlone ? `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a concise, evidence-backed explanation. Do NOT suggest or pick a chapter for the question.${coursePriorityInstruction}

    Rules and formatting (READ CAREFULLY):
    - You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
    - Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
    - Each output object MUST contain these keys exactly:
        {
            "question_idx": number,            // integer index matching the input question_idx
            "explanation": string,             // a detailed explanatory paragraph using ONLY the provided snippets as evidence. If it is maths or physics question, show your work and use LaTeX for formulas. Format explanation with simple HTML where helpful.
            "references": [string],            // array of reference strings; each reference MUST use this HTML format: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\"" or "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span><span style=\"color:#49c6fc\">page {page_no}</span>"; if no evidence, return an empty array.
            "corrected_answer": string         // A/B/C/D label if you determine a correction is needed, otherwise empty string
        }

    Evidence rules:
    - Use only the text snippets and page numbers provided in the hits as evidence. If a snippet directly answers the question, include a short quoted excerpt (verbatim) in the references. If a snippet is relevant but not direct, include only the reference without excerpt.
    - Do NOT invent page numbers, book names, or make up direct quotes. If the evidence is insufficient, set references to an empty array and produce the best possible explanation.

    Input (array):
    ${JSON.stringify(batchPayload, null, 2)}

    Respond with the STRICT JSON array matching the schema above. Ensure arrays and strings are valid JSON.` : `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a concise, evidence-backed recommendation describing the best matching chapter and a detailed explanation.

    Rules and formatting (READ CAREFULLY):
    - You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
    - Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
    - Each output object MUST contain these keys exactly:
        {
            "question_idx": number,            // integer index matching the input question_idx
            "chapter_name": string | null,     // human-friendly chapter/unit name if identifiable, else null
            "chapter_id": number | null,       // textbook chapter id (if available) or null
            "explanation": string,             // a detailed explanatory paragraph using ONLY the provided snippets as evidence. No prefatory phrases. If it is maths or physics question, show your work. if the explanaton has formula, use latex format. format the explanation(using html tags) to a format that easly understandable specially for calculations. 
            "references": [string],            // array of reference strings; each reference MUST use this HTML format: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\"" or "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span><span style=\"color:#49c6fc\">page {page_no}</span>"; if no evidence, return an empty array.
            "corrected_answer": string         // A/B/C/D label if you determine a correction is needed, otherwise empty string
        }

    Evidence rules:
    - Use only the text snippets and page numbers provided in the hits as evidence. If a snippet directly answers the question, include a short quoted excerpt (verbatim) in the references. If a snippet is relevant but not direct, include only the reference without excerpt.
    - Do NOT invent page numbers, book names, or make up direct quotes. If the evidence is insufficient, set chapter_name and chapter_id to null, generate explanation from your knowledge base, references to an empty array, and corrected_answer to an empty string.

    Input (array):
    ${JSON.stringify(batchPayload, null, 2)}

    Respond with the STRICT JSON array matching the schema above. Ensure arrays and strings are valid JSON.`

                            let genResp: any = null
                            try {
                                genResp = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: prompt })
                            } catch (gerr: any) {
                                console.warn('Gemini batch call failed', gerr)
                                genResp = null
                            }

                            const parseModelResponse = (r: any): string | null => {
                                if (!r) return null
                                if (typeof r === 'string') return r
                                if (typeof r.text === 'string') return r.text
                                if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content
                                if (Array.isArray(r.outputs) && r.outputs.length) {
                                    const out0 = r.outputs[0]
                                    if (typeof out0 === 'string') return out0
                                    if (out0?.content && typeof out0.content === 'object') {
                                        if (typeof out0.content?.text === 'string') return out0.content.text
                                        if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text
                                    }
                                }
                                return null
                            }

                            const outText = parseModelResponse(genResp)
                            if (outText) {
                                try {
                                    const out = outText
                                    const start = out.indexOf('[')
                                    const end = out.lastIndexOf(']')
                                    const jsonStr = start >= 0 && end > start ? out.slice(start, end + 1) : out
                                    const parsedArr = JSON.parse(jsonStr)
                                    if (Array.isArray(parsedArr)) {
                                        parsedArr.forEach((item: any) => {
                                            try {
                                                const qidx = item?.question_idx
                                                if (qidx == null) return

                                                const normalized: any = { ...item }
                                                normalized.explanation = String(item?.explanation ?? item?.explanation_text ?? item?.explain ?? item?.text ?? '')
                                                if (Array.isArray(item?.references)) {
                                                    normalized.references = item.references
                                                } else if (item?.references) {
                                                    normalized.references = [String(item.references)]
                                                } else if (Array.isArray(item?.refs)) {
                                                    normalized.references = item.refs
                                                } else if (item?.refs) {
                                                    normalized.references = [String(item.refs)]
                                                } else if (item?.reference) {
                                                    normalized.references = [String(item.reference)]
                                                } else {
                                                    normalized.references = []
                                                }

                                                if ((!normalized.references || normalized.references.length === 0) && normalized.explanation) {
                                                    try {
                                                        const expl = String(normalized.explanation)
                                                        const refs: string[] = []
                                                        const pattern = /(<span[^>]*>[^<]*<\/span>\s*-\s*<span[^>]*>[^<]*<\/span>\s*(?::\s*"[^"]*"?)?)/gi
                                                        let m: RegExpExecArray | null
                                                        while ((m = pattern.exec(expl))) {
                                                            refs.push(m[1])
                                                        }
                                                        if (refs.length === 0) {
                                                            const pagePat = /(<span[^>]*>[^<]*<\/span>\s*<span[^>]*>page\s*\d+[^<]*<\/span>)/gi
                                                            while ((m = pagePat.exec(expl))) {
                                                                refs.push(m[1])
                                                            }
                                                        }
                                                        if (refs.length) normalized.references = refs
                                                    } catch (e) {
                                                    }
                                                }
                                                normalized.corrected_answer = String(item?.corrected_answer ?? item?.correctedAnswer ?? item?.corrected ?? '')
                                                normalized.determined_course = item?.determined_course ?? item?.determinedCourse ?? item?.course ?? null
                                                normalized.chapter_id = item?.chapter_id ?? item?.chapterId ?? (item?.chapter && item.chapter.id) ?? null

                                                setLocalQuestions(prev => {
                                                    const copy = [...prev]
                                                    const cur = copy[qidx] || {}
                                                    const currentRag = (cur && cur.ragAssignment) ? cur.ragAssignment : {}
                                                    const suggestedTitle = (normalized?.chapter_name && String(normalized.chapter_name).trim()) ? String(normalized.chapter_name) : (currentRag.title || currentRag.label || '')
                                                    const suggestedTextbookId = normalized?.chapter_id != null ? Number(normalized.chapter_id) : (currentRag.textbookChapterId ?? currentRag.apiChapterId ?? null)
                                                    const suggestedApiId = normalized?.api_chapter_id != null ? Number(normalized.api_chapter_id) : (currentRag.apiChapterId ?? null)
                                                    copy[qidx] = {
                                                        ...cur,
                                                        ragAssignment: {
                                                            ...currentRag,
                                                            geminiSuggestion: normalized,
                                                            title: suggestedTitle,
                                                            label: suggestedTitle,
                                                            textbookChapterId: suggestedTextbookId,
                                                            apiChapterId: suggestedApiId,
                                                            status: currentRag.status || 'done'
                                                        }
                                                    }
                                                    return copy
                                                })
                                            } catch (e) {
                                            }
                                        })
                                    }
                                } catch (perr) {
                                    console.warn('Failed to parse Gemini batch JSON', perr)
                                }
                            } else {
                                console.warn('Gemini batch returned empty or unexpected shape', genResp)
                            }
                    }

                    if (imageItems.length > 0) {
                        for (const p of imageItems) {
                            try {
                                const q = localQuestions[p.idx]
                                const singlePayload = [{ question_idx: p.idx, question: p.question, choices: p.choices, student_answer: p.answer, hits: p.hitsPayload }]
                                const courseNames = Array.from(new Set((allRags || []).flatMap((b: any) => (b && b.course_names) ? b.course_names : []))).filter(Boolean)
                                const coursePriorityInstruction = isCourseIdsRequest && courseNames.length ? `\nCourse prioritization: the following courses are in-scope: ${JSON.stringify(courseNames)}. For each question, first output a field \"determined_course\" with the chosen course name (one of the list) or null if none. When producing explanation and references, prioritize hits coming from book(s) associated with the determined_course.` : ''

                                const singlePrompt = chapterAlone ? `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a concise, evidence-backed explanation. Do NOT suggest or pick a chapter for the question.${coursePriorityInstruction}

Rules and formatting (READ CAREFULLY):
- You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
- Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
- Each output object MUST contain these keys exactly:
    {
        "question_idx": number,    // integer index matching the input question_idx
        "explanation": string,     // // a detailed explanatory paragraph using ONLY the provided snippets as evidence. No prefatory phrases. If it is maths or physics question, show your work. if the explanaton has formula, use latex format. format the explanation(using html tags) to a format that easly understandable specially for calculations.
        "references": [string],    // array of reference strings; each reference MUST use this HTML format: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\"" or "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span><span style=\"color:#49c6fc\">page {page_no}</span>"; if no evidence, return an empty array.
        "corrected_answer": string   // A/B/C/D label if you determine a correction is needed, otherwise empty string
    }

Input (array):
${JSON.stringify(singlePayload, null, 2)}

Respond with the STRICT JSON array matching the schema above.` : `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a concise, evidence-backed recommendation describing the best matching chapter and a detailed explanation.

Rules and formatting (READ CAREFULLY):
- You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
- Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
- Each output object MUST contain these keys exactly:
    {
        "question_idx": number,            // integer index matching the input question_idx
        "chapter_name": string | null,     // human-friendly chapter/unit name if identifiable, else null
        "chapter_id": number | null,       // textbook chapter id (if available) or null
        "explanation": string,             // a detailed explanatory paragraph using ONLY the provided snippets as evidence. No prefatory phrases. If it is maths or physics question, show your work. if the explanaton has formula, use latex format. format the explanation(using html tags) to a format that easly understandable specially for calculations.
        "references": [string],            // array of reference strings; each reference MUST use this HTML format: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\"" or "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span><span style=\"color:#49c6fc\">page {page_no}</span>"; if no evidence, return an empty array.
        "corrected_answer": string         // A/B/C/D label if you determine a correction is needed, otherwise empty string
    }

Input (array):
${JSON.stringify(singlePayload, null, 2)}

Respond with the STRICT JSON array matching the schema above.`

                                const contents: any[] = [singlePrompt]

                                if (q.pendingImageFile) {
                                    try {
                                        const file = q.pendingImageFile as File
                                        const uploadedFile = await ai.files.upload({ file, config: { displayName: `${String(examId || q.exam_id || '')}_qimg_${p.idx}` } })
                                        let getFile = await ai.files.get({ name: uploadedFile.name as string })
                                        while (getFile.state === 'PROCESSING') {
                                            await new Promise(r => setTimeout(r, 1500))
                                            getFile = await ai.files.get({ name: uploadedFile.name as string })
                                        }
                                        if (getFile.state !== 'FAILED' && uploadedFile.uri) {
                                            try {
                                                contents.push(createPartFromUri(uploadedFile.uri, uploadedFile.mimeType || file.type || 'image/png'))
                                            } catch (e) {
                                                console.warn('createPartFromUri failed for uploaded image', e)
                                            }
                                        }
                                    } catch (e) {
                                        console.warn('Image upload to Gemini failed', e)
                                    }
                                } else if (q.image && typeof q.image === 'string') {
                                    try {
                                        contents.push(createPartFromUri(q.image, 'image/png'))
                                    } catch (e) {
                                        console.warn('createPartFromUri failed for existing image URL', e)
                                    }
                                }

                                let genRespSingle: any = null
                                try {
                                    genRespSingle = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents })
                                } catch (gerr: any) {
                                    console.warn('Gemini single-with-image call failed', gerr)
                                    genRespSingle = null
                                }

                                const parseModelResponseLocal = (r: any): string | null => {
                                    if (!r) return null
                                    if (typeof r === 'string') return r
                                    if (typeof r.text === 'string') return r.text
                                    if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content
                                    if (Array.isArray(r.outputs) && r.outputs.length) {
                                        const out0 = r.outputs[0]
                                        if (typeof out0 === 'string') return out0
                                        if (out0?.content && typeof out0.content === 'object') {
                                            if (typeof out0.content?.text === 'string') return out0.content.text
                                            if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text
                                        }
                                    }
                                    return null
                                }

                                const outTextSingle = parseModelResponseLocal(genRespSingle)
                                if (outTextSingle) {
                                    try {
                                        const out = outTextSingle
                                        const start = out.indexOf('[')
                                        const end = out.lastIndexOf(']')
                                        const jsonStr = start >= 0 && end > start ? out.slice(start, end + 1) : out
                                        const parsedArr = JSON.parse(jsonStr)
                                        if (Array.isArray(parsedArr)) {
                                            parsedArr.forEach((item: any) => {
                                                try {
                                                    const qidx = item?.question_idx
                                                    if (qidx == null) return

                                                    const normalized: any = { ...item }
                                                    normalized.explanation = String(item?.explanation ?? item?.explanation_text ?? item?.explain ?? item?.text ?? '')
                                                    if (Array.isArray(item?.references)) {
                                                        normalized.references = item.references
                                                    } else if (item?.references) {
                                                        normalized.references = [String(item.references)]
                                                    } else {
                                                        normalized.references = []
                                                    }
                                                    normalized.corrected_answer = String(item?.corrected_answer ?? item?.correctedAnswer ?? item?.corrected ?? '')
                                                    normalized.determined_course = item?.determined_course ?? item?.determinedCourse ?? item?.course ?? null
                                                    normalized.chapter_id = item?.chapter_id ?? item?.chapterId ?? (item?.chapter && item.chapter.id) ?? null

                                                    setLocalQuestions(prev => {
                                                        const copy = [...prev]
                                                        const cur = copy[qidx] || {}
                                                        const currentRag = (cur && cur.ragAssignment) ? cur.ragAssignment : {}
                                                        const suggestedTitle = (normalized?.chapter_name && String(normalized.chapter_name).trim()) ? String(normalized.chapter_name) : (currentRag.title || currentRag.label || '')
                                                        const suggestedTextbookId = normalized?.chapter_id != null ? Number(normalized.chapter_id) : (currentRag.textbookChapterId ?? currentRag.apiChapterId ?? null)
                                                        const suggestedApiId = normalized?.api_chapter_id != null ? Number(normalized.api_chapter_id) : (currentRag.apiChapterId ?? null)
                                                        copy[qidx] = {
                                                            ...cur,
                                                            ragAssignment: {
                                                                ...currentRag,
                                                                geminiSuggestion: normalized,
                                                                title: suggestedTitle,
                                                                label: suggestedTitle,
                                                                textbookChapterId: suggestedTextbookId,
                                                                apiChapterId: suggestedApiId,
                                                                status: currentRag.status || 'done'
                                                            }
                                                        }
                                                        return copy
                                                    })
                                                } catch (e) {
                                                }
                                            })
                                        }
                                    } catch (perr) {
                                        console.warn('Failed to parse Gemini single-with-image JSON', perr)
                                    }
                                } else {
                                    console.warn('Gemini single-with-image returned empty or unexpected shape', genRespSingle)
                                }
                            } catch (e) {
                                console.warn('Failed processing image item for Gemini', e)
                            }
                        }
                    }
                } catch (e) {
                    console.warn('Gemini batch processing failed', e)
                } finally {
                    pendingGemini.length = 0
                }
            }

            for (let ii = 0; ii < pageIndices.length; ii++) {
                const idx = pageIndices[ii]
                const q = localQuestions[idx];
                updateQuestionAt(idx, { ragAssignment: { status: 'running' } });
                try {
                    const rawChoices: string[] = (() => {
                        try {
                            if (Array.isArray(q.choices) && q.choices.length) {
                                return (q.choices as any[]).map(c => stripHtml(String(c || ''))).filter(Boolean);
                            }
                        } catch (e) {}
                        const fallbackFields = ['option1', 'option2', 'option3', 'option4', 'choice_a', 'choice_b', 'choice_c', 'choice_d'];
                        const opts: string[] = [];
                        for (const f of fallbackFields) {
                            const v = (q as any)[f];
                            if (v != null) {
                                const txt = stripHtml(String(v));
                                if (txt) opts.push(txt);
                            }
                        }
                        return opts;
                    })();

                    const text = `${stripHtml(q.question)}\n\nChoices:\n${rawChoices.map((c: any, i: number) => `${i + 1}. ${c}`).join('\n')}`;

                    const embData = await request('/api/proxy/embed/', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }, body: JSON.stringify({ text }) })
                    const embedding = embData?.embedding
                    if (!embedding) throw new Error('No embedding returned')

                    const vsData = await request('/api/textbook/vector-search/', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }, body: JSON.stringify({ book_ids: selectedRags, embedding, top_k: selectedRags.length * 5}) })
                    const hits = vsData?.results || [];

                    type Agg = { apiIdKey: string, apiChapterId?: number | null, textbookChapterId?: number | null, scoreSum: number, votes: number, snippets: string[] };
                    const aggMap: Record<string, Agg> = {};
                    for (const h of hits) {
                        const chapterObj = h.chapter;
                        const score = typeof h.score === 'number' ? h.score : parseFloat(String(h.score) || '0');
                        let apiId = null as number | null;
                        let textbookId = null as number | null;
                        if (chapterObj) {
                            textbookId = chapterObj.id;
                            apiId = textbookToApiMap[String(chapterObj.id)] ?? null;
                        }
                        const key = apiId != null ? `api:${apiId}` : (textbookId != null ? `txt:${textbookId}` : 'unknown');
                        if (!aggMap[key]) aggMap[key] = { apiIdKey: key, apiChapterId: apiId, textbookChapterId: textbookId, scoreSum: 0, votes: 0, snippets: [] };
                        aggMap[key].scoreSum += score;
                        aggMap[key].votes += 1;
                        if (h.chunk) {
                            if (typeof h.chunk === 'string') {
                                aggMap[key].snippets.push(h.chunk);
                            } else if (h.chunk && typeof h.chunk === 'object') {
                                const text = h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk);
                                aggMap[key].snippets.push(text);
                            } else {
                                aggMap[key].snippets.push(String(h.chunk));
                            }
                        }
                    }

                    const candidates = Object.values(aggMap);
                    let chosen: Agg | null = null;
                    if (candidates.length > 0) {
                        candidates.sort((a, b) => {
                            if (b.scoreSum !== a.scoreSum) return b.scoreSum - a.scoreSum;
                            return b.votes - a.votes;
                        });
                        chosen = candidates[0];
                    }

                    let assignedApiChapter: number | null = null
                    let assignedTextbookChapter: number | null = null
                    let assignedTitle = ''
                    let snippet = ''
                    let confidencePercent = 0
                    if (chosen) {
                        assignedApiChapter = chosen.apiChapterId ?? null
                        assignedTextbookChapter = chosen.textbookChapterId ?? null
                        let found = null as any
                        if (typeof assignedTextbookChapter !== 'undefined' && assignedTextbookChapter != null) {
                            for (const bid of selectedRags) {
                                const list = (perBookChapters && perBookChapters[String(bid)]) || []
                                const f = list.find((c: any) => c.id === assignedTextbookChapter)
                                if (f) { found = f; break }
                            }
                        }
                        if (!found && bookChapters && bookChapters.length) {
                            found = bookChapters.find((c: any) => (assignedTextbookChapter && c.id === assignedTextbookChapter) || (assignedApiChapter && c.api_chapter_id === assignedApiChapter))
                        }
                        if (!found && subjectChapters && subjectChapters.length) {
                            found = subjectChapters.find((c: any) => (assignedTextbookChapter && c.id === assignedTextbookChapter) || (assignedApiChapter && c.api_chapter_id === assignedApiChapter))
                        }
                        assignedTitle = found ? (found.title || `Chapter ${found.id}`) : (assignedTextbookChapter ? `Chapter ${assignedTextbookChapter}` : (assignedApiChapter ? `API Chapter ${assignedApiChapter}` : ''))
                        snippet = (chosen.snippets || []).slice(0, 2).join(' — ')

                        const allScores = candidates.map(c => typeof c.scoreSum === 'number' ? c.scoreSum : parseFloat(String(c.scoreSum) || '0'))
                        const totalScore = allScores.reduce((s, v) => s + (isNaN(v) ? 0 : v), 0)
                        if (totalScore > 0) {
                            confidencePercent = Math.round((chosen.scoreSum / totalScore) * 100)
                        } else {
                            confidencePercent = candidates.length === 1 ? 100 : 0
                        }
                    }
                    const baseAssignment: any = {
                        status: 'done',
                        apiChapterId: assignedApiChapter,
                        textbookChapterId: assignedTextbookChapter,
                        label: assignedTitle,
                        title: assignedTitle,
                        confidencePercent,
                        snippet,
                        accepted: null,
                        applied: false,
                    }

                    if (geminiKey) {
                        try {
                            const hitsPayload = hits.map((h: any, i: number) => {
                                const chapterName = h.chapter?.title || h.chapter?.name || (h.chapter?.id ? `Chapter ${h.chapter.id}` : '')
                                const text = h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || '')
                                const bookEntry = (allRags || []).find((b: any) => Number(b.id) === Number(h.book_id))
                                const bookCourseNames = bookEntry ? (bookEntry.course_names || (bookEntry.associations ? (bookEntry.associations.map((a: any) => a.api_course_name).filter(Boolean)) : [])) : []
                                return {
                                    rank: i + 1,
                                    score: h.score,
                                    book_id: h.book_id ?? null,
                                    book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                                    book_course_names: bookCourseNames,
                                    chapter: chapterName,
                                    snippet: text,
                                    page_no: h.chunk?.page_no ?? null,
                                    textbook_chapter_id: h.chapter?.id ?? null,
                                    api_chapter_id: h.chapter?.api_chapter_id ?? null,
                                }
                            })

                            const qChoices = rawChoices.map((c: any, i: number) => ({ idx: i + 1, text: String(c) }))
                            const answer = q.answer ?? q.ans ?? q.correct_ans ?? ''

                            const hasImage = !!(q.pendingImageFile || q.image)
                            pendingGemini.push({ idx, question: stripHtml(q.question), choices: qChoices, answer, hitsPayload, hasImage })
                            if (pendingGemini.length >= Math.max(1, Math.min(10, geminiBatchSize))) {
                                await flushGeminiBatch()
                            }
                        } catch (outerErr) {
                            console.warn('Gemini batch enqueue failed', outerErr)
                            baseAssignment.geminiError = String(outerErr)
                        }
                    }

                    updateQuestionAt(idx, { ragAssignment: baseAssignment })
                    setRagProgress(prev => ({ total: prev.total, completed: prev.completed + 1 }))
                } catch (err: any) {
                    console.error('RAG per-question failed', err)
                    const msg = err && (err.message || String(err))
                    updateQuestionAt(idx, { ragAssignment: { status: 'failed', error: msg, accepted: false } })
                    setRagProgress(prev => ({ total: prev.total, completed: prev.completed + 1 }))
                }
            }

            try {
                await flushGeminiBatch()
            } catch (e) {
                console.warn('Final Gemini flush failed', e)
            }

            setShowRagModal(false)
        } catch (err) {
            console.error('RAG run failed', err)
            alert('RAG search failed. See console for details.')
        } finally {
            setIsRunningRag(false)
        }
    }

    const runRagForSingleQuestion = useCallback(async (idx: number) => {
        const currentQuestions = localQuestionsRef.current
        if (!selectedRags || selectedRags.length === 0) {
            setShowRagModal(true)
            fetchRags()
            showToast('Please select one or more books first', 'error')
            return
        }
        setRunningSingleRag(idx)
        try {
            const perBookChapters: Record<string, any[]> = {}
            for (const bookId of selectedRags) {
                try {
                    const chaptersData = await request(`/api/textbook/${bookId}/chapters/`, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
                    perBookChapters[String(bookId)] = chaptersData.chapters || []
                } catch (e) {
                    perBookChapters[String(bookId)] = []
                }
            }
            const textbookToApiMap: Record<string, number | null> = {}
            Object.values(perBookChapters).forEach((chapterList: any[]) => {
                chapterList.forEach((c: any) => { textbookToApiMap[String(c.id)] = c.api_chapter_id || null })
            })
            setPerBookChaptersMap(perBookChapters)
            setBookChapters(Object.values(perBookChapters).flat())

            const q = currentQuestions[idx]
            updateQuestionAt(idx, { ragAssignment: { status: 'running' } })
            try {
                const rawChoices: string[] = (() => {
                    try {
                        if (Array.isArray(q.choices) && q.choices.length) {
                            return (q.choices as any[]).map(c => stripHtml(String(c || ''))).filter(Boolean)
                        }
                    } catch (e) {}
                    const fallbackFields = ['option1', 'option2', 'option3', 'option4', 'choice_a', 'choice_b', 'choice_c', 'choice_d']
                    const opts: string[] = []
                    for (const f of fallbackFields) {
                        const v = (q as any)[f]
                        if (v != null) {
                            const txt = stripHtml(String(v))
                            if (txt) opts.push(txt)
                        }
                    }
                    return opts
                })()

                const text = `${stripHtml(q.question)}\n\nChoices:\n${rawChoices.map((c: any, i: number) => `${i + 1}. ${c}`).join('\n')}`

                const embData = await request('/api/proxy/embed/', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }, body: JSON.stringify({ text }) })
                const embedding = embData?.embedding
                if (!embedding) throw new Error('No embedding returned')

                const vsData = await request('/api/textbook/vector-search/', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' }, body: JSON.stringify({ book_ids: selectedRags, embedding, top_k: selectedRags.length * 5}) })
                const hits = vsData?.results || []

                type Agg = { apiIdKey: string, apiChapterId?: number | null, textbookChapterId?: number | null, scoreSum: number, votes: number, snippets: string[] }
                const aggMap: Record<string, Agg> = {}
                for (const h of hits) {
                    const chapterObj = h.chapter
                    const score = typeof h.score === 'number' ? h.score : parseFloat(String(h.score) || '0')
                    let apiId = null as number | null
                    let textbookId = null as number | null
                    if (chapterObj) {
                        textbookId = chapterObj.id
                        apiId = textbookToApiMap[String(chapterObj.id)] ?? null
                    }
                    const key = apiId != null ? `api:${apiId}` : (textbookId != null ? `txt:${textbookId}` : 'unknown')
                    if (!aggMap[key]) aggMap[key] = { apiIdKey: key, apiChapterId: apiId, textbookChapterId: textbookId, scoreSum: 0, votes: 0, snippets: [] }
                    aggMap[key].scoreSum += score
                    aggMap[key].votes += 1
                    if (h.chunk) {
                        if (typeof h.chunk === 'string') {
                            aggMap[key].snippets.push(h.chunk)
                        } else if (h.chunk && typeof h.chunk === 'object') {
                            const text = h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)
                            aggMap[key].snippets.push(text)
                        } else {
                            aggMap[key].snippets.push(String(h.chunk))
                        }
                    }
                }

                const candidates = Object.values(aggMap)
                let chosen: Agg | null = null
                if (candidates.length > 0) {
                    candidates.sort((a, b) => {
                        if (b.scoreSum !== a.scoreSum) return b.scoreSum - a.scoreSum
                        return b.votes - a.votes
                    })
                    chosen = candidates[0]
                }

                let assignedApiChapter: number | null = null
                let assignedTextbookChapter: number | null = null
                let assignedTitle = ''
                let snippet = ''
                let confidencePercent = 0
                if (chosen) {
                    assignedApiChapter = chosen.apiChapterId ?? null
                    assignedTextbookChapter = chosen.textbookChapterId ?? null
                    let found = null as any
                    if (typeof assignedTextbookChapter !== 'undefined' && assignedTextbookChapter != null) {
                        for (const bid of selectedRags) {
                            const list = (perBookChapters && perBookChapters[String(bid)]) || []
                            const f = list.find((c: any) => c.id === assignedTextbookChapter)
                            if (f) { found = f; break }
                        }
                    }
                    if (!found && bookChapters && bookChapters.length) {
                        found = bookChapters.find((c: any) => (assignedTextbookChapter && c.id === assignedTextbookChapter) || (assignedApiChapter && c.api_chapter_id === assignedApiChapter))
                    }
                    if (!found && subjectChapters && subjectChapters.length) {
                        found = subjectChapters.find((c: any) => (assignedTextbookChapter && c.id === assignedTextbookChapter) || (assignedApiChapter && c.api_chapter_id === assignedApiChapter))
                    }
                    assignedTitle = found ? (found.title || `Chapter ${found.id}`) : (assignedTextbookChapter ? `Chapter ${assignedTextbookChapter}` : (assignedApiChapter ? `API Chapter ${assignedApiChapter}` : ''))
                    snippet = (chosen.snippets || []).slice(0, 2).join(' — ')

                    const allScores = candidates.map(c => typeof c.scoreSum === 'number' ? c.scoreSum : parseFloat(String(c.scoreSum) || '0'))
                    const totalScore = allScores.reduce((s, v) => s + (isNaN(v) ? 0 : v), 0)
                    if (totalScore > 0) {
                        confidencePercent = Math.round((chosen.scoreSum / totalScore) * 100)
                    } else {
                        confidencePercent = candidates.length === 1 ? 100 : 0
                    }
                }
                const baseAssignment: any = {
                    status: 'done',
                    apiChapterId: assignedApiChapter,
                    textbookChapterId: assignedTextbookChapter,
                    label: assignedTitle,
                    title: assignedTitle,
                    confidencePercent,
                    snippet,
                    accepted: null,
                    applied: false,
                }

                if (geminiKey) {
                    try {
                        const hitsPayload = hits.map((h: any, i: number) => {
                            const chapterName = h.chapter?.title || h.chapter?.name || (h.chapter?.id ? `Chapter ${h.chapter.id}` : '')
                            const text = h.chunk && typeof h.chunk === 'object' ? (h.chunk.text_content || h.chunk.text || JSON.stringify(h.chunk)) : String(h.chunk || '')
                            const bookEntry = (allRags || []).find((b: any) => Number(b.id) === Number(h.book_id))
                            const bookCourseNames = bookEntry ? (bookEntry.course_names || (bookEntry.associations ? (bookEntry.associations.map((a: any) => a.api_course_name).filter(Boolean)) : [])) : []
                            return {
                                rank: i + 1,
                                score: h.score,
                                book_id: h.book_id ?? null,
                                book_name: h.book_title ?? h.book?.title ?? h.book_name ?? null,
                                book_course_names: bookCourseNames,
                                chapter: chapterName,
                                snippet: text,
                                page_no: h.chunk?.page_no ?? null,
                                textbook_chapter_id: h.chapter?.id ?? null,
                                api_chapter_id: h.chapter?.api_chapter_id ?? null,
                            }
                        })

                        const qChoices = rawChoices.map((c: any, i: number) => ({ idx: i + 1, text: String(c) }))
                        const answer = q.answer ?? q.ans ?? q.correct_ans ?? ''

                        const ai = new GoogleGenAI({ apiKey: geminiKey.trim() })
                        const batchPayload = [{ idx, question: stripHtml(q.question), choices: qChoices, answer, hitsPayload }]
                        const courseNames = Array.from(new Set((allRags || []).flatMap((b: any) => (b && b.course_names) ? b.course_names : []))).filter(Boolean)
                        const coursePriorityInstruction = isCourseIdsRequest && courseNames.length ? `\nCourse prioritization: the following courses are in-scope: ${JSON.stringify(courseNames)}. For each question, first output a field \"determined_course\" with the chosen course name (one of the list) or null if none. When producing explanation and references, prioritize hits coming from book(s) associated with the determined_course.` : ''

                        const prompt = chapterAlone ? `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a concise, evidence-backed explanation. Do NOT suggest or pick a chapter for the question.${coursePriorityInstruction}

                        Rules and formatting (READ CAREFULLY):
                        - You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
                        - Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
                        - Each output object MUST contain these keys exactly:
                            {
                                "question_idx": number,           // integer index matching the input question_idx
                                "explanation": string,            // a detailed explanatory paragraph using ONLY the provided snippets as evidence. No prefatory phrases. If it is maths or physics question, show your work. if the explanaton has formula, use latex format. format the explanation(using html tags) to a format that easly understandable specially for calculations.
                                "references": [string],           // array of reference strings; each reference MUST use this HTML format: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\"" or "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span><span style=\"color:#49c6fc\">page {page_no}</span>"; if no evidence, return an empty array.
                                "corrected_answer": string        // A/B/C/D label if you determine a correction is needed, otherwise empty string. if the answer is right, leave this null
                            }

                        Evidence rules:
                        - Use only the text snippets and page numbers provided in the hits as evidence. If a snippet directly answers the question, include a short quoted excerpt (verbatim) in the references. If a snippet is relevant but not direct, include only the reference without excerpt.
                        - Do NOT invent page numbers, book names, or make up direct quotes. If the evidence is insufficient, set references to an empty array and produce the best possible explanation.

                        Input (array):
                        ${JSON.stringify(batchPayload, null, 2)}

                        Respond with the STRICT JSON array matching the schema above. Ensure arrays and strings are valid JSON.` : `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a concise, evidence-backed recommendation describing the best matching chapter and a detailed explanation.

                        Rules and formatting (READ CAREFULLY):
                        - You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
                        - Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
                        - Each output object MUST contain these keys exactly:
                            {
                                "question_idx": number,            // integer index matching the input question_idx
                                "chapter_name": string | null,     // human-friendly chapter/unit name if identifiable, else null
                                "chapter_id": number | null,       // textbook chapter id (if available) or null
                                "explanation": string,             // a detailed explanatory paragraph using ONLY the provided snippets as evidence. No prefatory phrases. If it is maths or physics question, show your work. if the explanaton has formula, use latex format. format the explanation(using html tags) to a format that easly understandable specially for calculations.
                                "references": [string],            // array of reference strings; each reference MUST use this HTML format: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\"" or "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span><span style=\"color:#49c6fc\">page {page_no}</span>"; if no evidence, return an empty array.
                                "corrected_answer": string         // A/B/C/D label if you determine a correction is needed, otherwise empty string. if the answer is right, leave this null
                            }

                        Evidence rules:
                        - Use only the text snippets and page numbers provided in the hits as evidence. If a snippet directly answers the question, include a short quoted excerpt (verbatim) in the references. If a snippet is relevant but not direct, include only the reference without excerpt.
                        - Do NOT invent page numbers, book names, or make up direct quotes. If the evidence is insufficient, set chapter_name and chapter_id to null, generate explanation from your knowledge base, references to an empty array, and corrected_answer to an empty string.

                        Brevity and style:
                        - Explanations must be detailed, factual, and avoid meta-phrases such as "based on the provided text". Do not repeat the question.

                        Input (array):
                        ${JSON.stringify(batchPayload, null, 2)}

                        Respond with the STRICT JSON array matching the schema above. Ensure arrays and strings are valid JSON.`

                        const contents: any[] = [prompt]

                        if (q.pendingImageFile) {
                            try {
                                const file = q.pendingImageFile as File
                                const uploadedFile = await ai.files.upload({ file, config: { displayName: `${String(examId || q.exam_id || '')}_qimg_${idx}` } })
                                let getFile = await ai.files.get({ name: uploadedFile.name as string })
                                while (getFile.state === 'PROCESSING') {
                                    await new Promise(r => setTimeout(r, 1500))
                                    getFile = await ai.files.get({ name: uploadedFile.name as string })
                                }
                                if (getFile.state !== 'FAILED' && uploadedFile.uri) {
                                    try {
                                        contents.push(createPartFromUri(uploadedFile.uri, uploadedFile.mimeType || file.type || 'image/png'))
                                    } catch (e) {
                                        console.warn('createPartFromUri failed for uploaded image', e)
                                    }
                                }
                            } catch (e) {
                                console.warn('Image upload to Gemini failed', e)
                            }
                        } else if (q.image && typeof q.image === 'string') {
                            try {
                                contents.push(createPartFromUri(q.image, 'image/png'))
                            } catch (e) {
                                console.warn('createPartFromUri failed for existing image URL', e)
                            }
                        }

                        let genRespSingle: any = null
                        try {
                            genRespSingle = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents })
                        } catch (gerr: any) {
                            console.warn('Gemini single call failed', gerr)
                            genRespSingle = null
                        }

                        const parseModelResponseLocal = (r: any): string | null => {
                            if (!r) return null
                            if (typeof r === 'string') return r
                            if (typeof r.text === 'string') return r.text
                            if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content
                            if (Array.isArray(r.outputs) && r.outputs.length) {
                                const out0 = r.outputs[0]
                                if (typeof out0 === 'string') return out0
                                if (out0?.content && typeof out0.content === 'object') {
                                    if (typeof out0.content?.text === 'string') return out0.content.text
                                    if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text
                                }
                            }
                            return null
                        }

                        const outTextSingle = parseModelResponseLocal(genRespSingle)
                        if (outTextSingle) {
                            try {
                                const out = outTextSingle
                                const start = out.indexOf('[')
                                const end = out.lastIndexOf(']')
                                const jsonStr = start >= 0 && end > start ? out.slice(start, end + 1) : out
                                const parsedArr = JSON.parse(jsonStr)
                                if (Array.isArray(parsedArr) && parsedArr.length > 0) {
                                    const item = parsedArr[0]
                                    const qidx = item?.question_idx
                                    if (qidx === idx) {
                                        const cur = localQuestionsRef.current[qidx] || {}
                                        const currentRag = (cur && cur.ragAssignment) ? cur.ragAssignment : {}
                                        const normalized: any = { ...item }
                                        normalized.explanation = String(item?.explanation ?? item?.explanation_text ?? item?.explain ?? item?.text ?? '')
                                        if (Array.isArray(item?.references)) {
                                            normalized.references = item.references
                                        } else if (item?.references) {
                                            normalized.references = [String(item.references)]
                                        } else {
                                            normalized.references = []
                                        }
                                        normalized.corrected_answer = String(item?.corrected_answer ?? item?.correctedAnswer ?? item?.corrected ?? '')
                                        normalized.determined_course = item?.determined_course ?? item?.determinedCourse ?? item?.course ?? null
                                        normalized.chapter_id = item?.chapter_id ?? item?.chapterId ?? (item?.chapter && item.chapter.id) ?? null
                                        const suggestedTitle = (normalized?.chapter_name && String(normalized.chapter_name).trim()) ? String(normalized.chapter_name) : (currentRag.title || currentRag.label || '')
                                        const suggestedTextbookId = normalized?.chapter_id != null ? Number(normalized.chapter_id) : (currentRag.textbookChapterId ?? currentRag.apiChapterId ?? null)
                                        const suggestedApiId = normalized?.api_chapter_id != null ? Number(normalized.api_chapter_id) : (currentRag.apiChapterId ?? null)

                                        baseAssignment.geminiSuggestion = normalized;
                                        baseAssignment.title = suggestedTitle;
                                        baseAssignment.label = suggestedTitle;
                                        baseAssignment.textbookChapterId = suggestedTextbookId;
                                        baseAssignment.apiChapterId = suggestedApiId;
                                    }
                                }
                            } catch (perr) {
                                console.warn('Failed to parse Gemini single JSON', perr)
                            }
                        } else {
                            console.warn('Gemini single returned empty or unexpected shape', genRespSingle)
                        }
                    } catch (outerErr) {
                        console.warn('Gemini processing failed', outerErr)
                        baseAssignment.geminiError = String(outerErr)
                    }
                }

                updateQuestionAt(idx, { ragAssignment: baseAssignment })
            } catch (err: any) {
                console.error('RAG per-question failed', err)
                const msg = err && (err.message || String(err))
                updateQuestionAt(idx, { ragAssignment: { status: 'failed', error: msg, accepted: false } })
            }
        } catch (err) {
            console.error('RAG run failed', err)
            alert('RAG search failed. See console for details.')
        } finally {
            setRunningSingleRag(null)
        }
    }, [selectedRags, token, showToast, updateQuestionAt])

    const runChapterAssign = async () => {
        try {
            const raw = localStorage.getItem('add_exam_subjects')
            let courseIds: string[] = []
            if (raw) {
                try { courseIds = JSON.parse(raw) as string[] } catch { courseIds = (raw || '').split(',').map(s => s.trim()).filter(Boolean) }
            }
            if (!courseIds || courseIds.length === 0) {
                alert('No courses selected. Please select courses in MixedExam first.')
                return
            }
            if (!geminiKey) {
                alert('No Gemini key available for model calls.')
                return
            }

            setIsRunningChapterAssign(true)

            const courseChaptersMap: Record<string, Array<{ name: string; is_subchapter: boolean; chaporsubid: number }>> = {}
            const subToParent: Record<string, number> = {}
            for (const cid of courseIds) {
                try {
                    let data: any = { chapters: [] }
                    if (useChapterAndSubchapter) {
                        try {
                            data = await queryClient.fetchQuery<any>({ queryKey: ['course_chapter_options', cid], queryFn: () => getCourseChapterOptions(token ?? null, Number(cid)), staleTime: 5 * 60 * 1000 })
                        } catch (e) {
                            data = { chapters: [] }
                        }
                    } else {
                        try {
                            data = await queryClient.fetchQuery<any>({ queryKey: ['course_chapters', cid], queryFn: () => getCourseChapters(token ?? null, Number(cid)), staleTime: 5 * 60 * 1000 })
                        } catch (e) {
                            data = { chapters: [] }
                        }
                    }
                    const chs = data?.chapters || []
                    const arr: Array<any> = []
                    for (const ch of chs) {
                        if (useChapterAndSubchapter) {
                            if (Array.isArray(ch.subchapter_options) && ch.subchapter_options.length) {
                                for (const sc of ch.subchapter_options) {
                                    arr.push({ name: `${ch.name || ch.title || ''}${sc.name ? ' > ' + sc.name : ''}`, is_subchapter: true, chaporsubid: sc.id })
                                    try { subToParent[String(sc.id)] = Number(ch.id) } catch (e) {}
                                }
                            } else if (Array.isArray(ch.subchapters) && ch.subchapters.length) {
                                for (const sc of ch.subchapters) { arr.push({ name: `${ch.name || ch.title || ''}${sc.name ? ' > ' + sc.name : ''}`, is_subchapter: true, chaporsubid: sc.id }); try { subToParent[String(sc.id)] = Number(ch.id) } catch (e) {} }
                            } else {
                                arr.push({ name: `${ch.name || ch.title || `Chapter ${ch.id}`}`, is_subchapter: false, chaporsubid: ch.id })
                            }
                        } else {
                            if (Array.isArray(ch.subchapters) && ch.subchapters.length) {
                                for (const sc of ch.subchapters) { arr.push({ name: `${ch.title || ch.name || ''}${sc.name ? ' > ' + sc.name : ''}`, is_subchapter: true, chaporsubid: sc.id }); try { subToParent[String(sc.id)] = Number(ch.id) } catch (e) {} }
                            } else if (Array.isArray(ch.course_subchapters) && ch.course_subchapters.length) {
                                for (const sc of ch.course_subchapters) {
                                    arr.push({ name: `${ch.title || ch.name || ''}${sc.name ? ' > ' + sc.name : ''}`, is_subchapter: true, chaporsubid: sc.id })
                                }
                            } else {
                                arr.push({ name: `${ch.title || ch.name || `Chapter ${ch.id}`}`, is_subchapter: false, chaporsubid: ch.id })
                            }
                        }
                    }
                    courseChaptersMap[cid] = arr
                } catch (e) {
                    courseChaptersMap[cid] = []
                }
            }

            try {
                setSubToParent(prev => ({ ...prev, ...subToParent }))
            } catch (e) { }
            try {
                setCourseChapterOptionsMap(courseChaptersMap)
            } catch (e) { }

            const chaptersJson = courseChaptersMap

            let questionsToProcess: Array<any> = []
            if (singleChapterIndex != null) {
                const idx = singleChapterIndex
                const q = localQuestions[idx]
                if (q) {
                    questionsToProcess = [{ ques_id: q.id ?? `local_${idx}`, question: stripHtml(q.question), choices: Array.isArray(q.choices) ? q.choices.map((c: any) => stripHtml(String(c))) : [] }]
                }
            } else {
                questionsToProcess = localQuestions.map((q, idx) => ({ ques_id: q.id ?? `local_${idx}`, question: stripHtml(q.question), choices: Array.isArray(q.choices) ? q.choices.map((c: any) => stripHtml(String(c))) : [] }))
            }
            const batchSize = Math.max(1, Math.min(50, Number(chapterBatchSize) || 5))

            const ai = new GoogleGenAI({ apiKey: geminiKey.trim() })

            const parseModelResponse = (r: any): string | null => {
                if (!r) return null
                if (typeof r === 'string') return r
                if (typeof r.text === 'string') return r.text
                if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content
                if (Array.isArray(r.outputs) && r.outputs.length) {
                    const out0 = r.outputs[0]
                    if (typeof out0 === 'string') return out0
                    if (out0?.content && typeof out0.content === 'object') {
                        if (typeof out0.content?.text === 'string') return out0.content.text
                        if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text
                    }
                }
                return null
            }

            for (let p = 0; p < questionsToProcess.length; p += batchSize) {
                const batch = questionsToProcess.slice(p, p + batchSize)
                const prompt = `You are given an array of questions and a catalog of chapters/subchapters organized by course. The chapters catalog is: ${JSON.stringify(chaptersJson, null, 2)}\n\nFor each question in the input array, choose the single best chapter or subchapter (from the provided catalog) that the question belongs to and return strict JSON only: an array of objects with keys {"ques_id":<same id>,"chaporsubid":<id from the catalog>,"is_subchapter":<true|false>} in the same order. The is_subchapter flag indicates whether the provided id is a subchapter id (true) or a chapter id (false). \n\nInput questions:\n${JSON.stringify(batch, null, 2)}`
                let genResp: any = null
                try {
                    genResp = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents: prompt })
                } catch (gerr: any) {
                    console.warn('Gemini chapter-assign call failed', gerr)
                    genResp = null
                }

                const outText = parseModelResponse(genResp)
                if (!outText) {
                    console.warn('Gemini returned empty shape for chapter assign', genResp)
                    continue
                }
                try {
                    const start = outText.indexOf('[')
                    const end = outText.lastIndexOf(']')
                    const jsonStr = start >= 0 && end > start ? outText.slice(start, end + 1) : outText
                    const parsed = JSON.parse(jsonStr)
                    if (Array.isArray(parsed)) {
                        for (const item of parsed) {
                            try {
                                const qid = item?.ques_id
                                const csid = item?.chaporsubid ?? null
                                const itemIsSub = typeof item?.is_subchapter === 'boolean' ? item.is_subchapter : null
                                const li = localQuestions.findIndex(lq => (lq.id != null && String(lq.id) === String(qid)) || (`local_${localQuestions.indexOf(lq)}` === String(qid)))
                                let targetIndex = li
                                if (targetIndex === -1) {
                                    const idxMatch = String(qid || '').match(/^local_(\d+)$/)
                                    if (idxMatch) targetIndex = Number(idxMatch[1])
                                }
                                if (targetIndex >= 0 && targetIndex < localQuestions.length) {
                                    const cur = localQuestions[targetIndex] || {}
                                    const currentRag = (cur && cur.ragAssignment) ? cur.ragAssignment : {}
                                    const newRag = { ...currentRag }
                                    newRag.assigned_chaporsubid = csid
                                    let foundName: string | null = null
                                    let foundIsSub = false
                                    if (itemIsSub !== null) {
                                        foundIsSub = !!itemIsSub
                                    }
                                    for (const [_cidK, arr] of Object.entries(chaptersJson)) {
                                        const found = (arr || []).find((x: any) => Number(x.chaptorsubid) === Number(csid))
                                        if (found) { foundName = found.name; if (itemIsSub === null) foundIsSub = !!found.is_subchapter; break }
                                    }
                                    newRag.assigned_name = foundName
                                    newRag.assigned_is_subchapter = foundIsSub
                                    if (foundIsSub) newRag.subchapterId = csid; else newRag.chapterId = csid
                                    const visiblePatch: any = {}
                                    try {
                                        if (useChapterAndSubchapter) {
                                            if (foundIsSub) {
                                                const parent = subToParent[String(csid)] || null
                                                if (parent) {
                                                    visiblePatch.chapter = String(parent)
                                                    visiblePatch.chapter_option = parent
                                                    try { fetchSubchaptersForId(Number(parent)) } catch (e) {}
                                                }
                                                visiblePatch.subchapter = String(csid)
                                                visiblePatch.subchapter_option = csid
                                            } else {
                                                visiblePatch.chapter = String(csid)
                                                visiblePatch.chapter_option = csid
                                                visiblePatch.subchapter = ''
                                                visiblePatch.subchapter_option = undefined
                                                try { fetchSubchaptersForId(Number(csid)) } catch (e) {}
                                            }
                                        } else {
                                            const mappedApi = mapToApiChapterId(csid)
                                            const mappedSubject = mapToSubjectChapterId(mappedApi ?? csid)
                                            const valueToSet = mappedApi != null ? String(mappedApi) : (mappedSubject != null ? String(mappedSubject) : (csid != null ? String(csid) : ''))
                                            visiblePatch.chapter = valueToSet
                                            try {
                                                const subjId = mappedSubject != null ? Number(mappedSubject) : (mappedApi != null ? Number(mappedApi) : Number(csid))
                                                if (!Number.isNaN(subjId) && subjId) fetchSubchaptersForId(subjId)
                                            } catch (e) {}
                                            if (foundIsSub) {
                                                visiblePatch.subchapter = String(csid)
                                            } else {
                                                visiblePatch.subchapter = ''
                                            }
                                        }
                                    } catch (e) {
                                    }

                                    setLocalQuestions(prev => {
                                        const copy = [...prev]
                                        const updated = { ...copy[targetIndex], ragAssignment: newRag, ...visiblePatch }
                                        copy[targetIndex] = updated
                                        return copy
                                    })
                                }
                            } catch (e) {
                            }
                        }
                    }
                } catch (e) {
                    console.warn('Failed parsing Gemini chapter assign response', e)
                }
            }

            setShowChapterModal(false)
            setSingleChapterIndex(null)
            showToast('Chapter assignment completed (results saved locally).', 'success')
        } catch (e) {
            console.error('Chapter assignment failed', e)
            showToast('Chapter assignment failed. See console.', 'error')
        } finally {
            setIsRunningChapterAssign(false)
        }
    }

    const buildPayload = useCallback(() => {
        return localQuestions.map((q: any) => {
            let chapterVal: any = q.chapter

            const resolvedApi = mapToApiChapterId(q.chapter)
            if (resolvedApi != null) {
                chapterVal = resolvedApi
            } else if (q.ragAssignment && q.ragAssignment.acceptedChapter) {
                const gemChapter = q.ragAssignment?.geminiSuggestion?.chapter_id
                const chosen = gemChapter != null ? gemChapter : (q.ragAssignment.apiChapterId ?? q.ragAssignment.textbookChapterId)
                const resolvedFromRagApi = mapToApiChapterId(chosen)
                chapterVal = resolvedFromRagApi != null ? resolvedFromRagApi : (chosen != null ? Number(chosen) : q.chapter)
            } else {
                chapterVal = q.chapter
            }

            let referenceVal = ''
            if (q.reference && String(q.reference).trim()) referenceVal = String(q.reference)
            else if (q.ragAssignment && q.ragAssignment.geminiSuggestion && q.ragAssignment.geminiSuggestion.references) {
                const refs = q.ragAssignment.geminiSuggestion.references
                referenceVal = Array.isArray(refs) ? refs.join(' <br> ') : String(refs)
            }

            const body: any = {
                ques: q.question ?? q.ques ?? '',
                option1: q.choice_a ?? q.option1 ?? '',
                option2: q.choice_b ?? q.option2 ?? '',
                option3: q.choice_c ?? q.option3 ?? '',
                option4: q.choice_d ?? q.option4 ?? '',
                correct_ans: (q.corrected_answer ?? q.correctedAnswer ?? q.ans ?? q.answer ?? q.correct_ans ?? ''),

                explanation: q.explanation ?? '',
                note: q.note ?? '',
                chapter: (chapterVal !== null && chapterVal !== undefined && String(chapterVal).trim() !== '') ? Number(chapterVal) : '',
                reference: referenceVal,
                corrected_answer: q.corrected_answer ?? q.correctedAnswer ?? '',

                ...(q.id ? { id: q.id } : {}),
                original: { ...q }
            }
            if (useChapterAndSubchapter && targetLevel === 'college_university') {
                const opt: any = { is_chapteroption: true }
                if (q.subchapter && String(q.subchapter).trim()) {
                    opt.subchapter_option = Number(q.subchapter)
                    const parent = subToParent[String(q.subchapter)]
                    if (parent) opt.chapter_option = Number(parent)
                    else if (q.chapter && String(q.chapter).trim()) opt.chapter_option = Number(q.chapter)
                } else if (chapterVal !== null && chapterVal !== undefined && String(chapterVal).trim() !== '') {
                    opt.chapter_option = Number(chapterVal)
                }
                delete body.chapter
                Object.assign(body, opt)
            } else {
            }

            return body
        })
    }, [localQuestions, subjectChapters, bookChapters, useChapterAndSubchapter, subToParent, targetLevel, mapToApiChapterId])

    const saveFn = useCallback(async () => {
        if (isSaving) return
        setIsSaving(true)

        try {
            const missing: { idx: number; chapterMissing: boolean; explanationMissing: boolean }[] = []
            localQuestions.forEach((q: any, i: number) => {
                let chapterOk = false
                try {
                    if (useChapterAndSubchapter) {
                        chapterOk = !!(
                            (q.chapter_option != null && String(q.chapter_option).trim() !== '') ||
                            (q.subchapter_option != null && String(q.subchapter_option).trim() !== '') ||
                            (q.chapter != null && String(q.chapter).trim() !== '') ||
                            (q.ragAssignment && (q.ragAssignment.acceptedChapter || q.ragAssignment.accepted))
                        )
                    } else {
                        chapterOk = !!(
                            (q.chapter != null && String(q.chapter).trim() !== '') ||
                            (q.ragAssignment && (q.ragAssignment.acceptedChapter || q.ragAssignment.accepted))
                        )
                    }
                } catch (e) {
                    chapterOk = false
                }

                const explanationOk = !!(q.explanation && String(q.explanation).trim()) || (q.ragAssignment && (q.ragAssignment.acceptedExplanation || q.ragAssignment.accepted))
                if (!chapterOk || !explanationOk) missing.push({ idx: i, chapterMissing: !chapterOk, explanationMissing: !explanationOk })
            })

            if (missing.length > 0) {
                const msg = missing.map(m => `Q${m.idx + 1}: ${m.chapterMissing ? 'chapter' : ''}${m.chapterMissing && m.explanationMissing ? ' & ' : ''}${m.explanationMissing ? 'explanation' : ''}`).join('\n')
                showToast('Please complete chapter and explanation for all questions', 'error')
                alert(`Please complete the following before saving:\n${msg}`)
                return
            }

            const payload = buildPayload()

            const saveResp = await onSave(payload)

            const anyResp: any = (saveResp as any) || {}
            const savedIds: Array<number | null> = Array.isArray(anyResp?.data?.saved_ids) ? anyResp.data.saved_ids : (Array.isArray(anyResp?.saved_ids) ? anyResp.saved_ids : (Array.isArray(anyResp?.data?.result?.saved_ids) ? anyResp.data.result.saved_ids : []))

            const uploadPromises: Array<Promise<void>> = []
            localQuestions.forEach((q: any, i: number) => {
                if (q.pendingImageFile) {
                    const file = q.pendingImageFile as File
                    uploadPromises.push((async () => {
                        try {
                            const form = new FormData()
                            form.append('image', file)
                            const maybeId = (Array.isArray(savedIds) && savedIds.length > i) ? savedIds[i] : (q.id || null)
                            if (maybeId) form.append('question_id', String(maybeId))
                            const targetExamId = String(examId || q.exam_id || '')
                            try {
                                const data = await request(`/api/school/exam-detail/${targetExamId}/upload-question-image/`, { method: 'POST', headers: { Authorization: token ? `Bearer ${token}` : '' }, body: form })
                                const img = data?.image
                                updateQuestionAt(i, { image: img })
                                updateQuestionAt(i, { pendingImageFile: undefined })
                            } catch (e) {
                                console.warn('Image upload failed for question', i)
                            }
                        } catch (e) {
                            console.warn('Image upload exception', e)
                        }
                    })())
                }
            })

            if (uploadPromises.length > 0) await Promise.all(uploadPromises)
        } catch (err) {
            console.error('Save failed', err)
            showToast('Failed to save questions. See console for details.', 'error')
            throw err
        } finally {
            setIsSaving(false)
        }
    }, [isSaving, localQuestions, onSave, buildPayload, useChapterAndSubchapter, showToast, examId, token, updateQuestionAt])

    useEffect(() => {
        if (onRegisterSave) {
            onRegisterSave(saveFn)
        }
    }, [onRegisterSave, saveFn])

    const getPageIndices = useCallback(() => {
        const start = page * PAGE_SIZE
        const end = Math.min(start + PAGE_SIZE, localQuestions.length)
        const arr: number[] = []
        for (let i = start; i < end; i++) arr.push(i)
        return { start, end, indices: arr }
    }, [page, PAGE_SIZE, localQuestions.length])

    const saveAndNext = useCallback(async () => {
        if (isSaving) return
        const { indices } = getPageIndices()
        if (!indices || indices.length === 0) return
        setIsSaving(true)
        try {
            const missing: { idx: number; chapterMissing: boolean; explanationMissing: boolean }[] = []
            for (const i of indices) {
                const q = localQuestions[i]
                let chapterOk = false
                try {
                    if (useChapterAndSubchapter) {
                        chapterOk = !!(
                            (q.chapter_option != null && String(q.chapter_option).trim() !== '') ||
                            (q.subchapter_option != null && String(q.subchapter_option).trim() !== '') ||
                            (q.chapter != null && String(q.chapter).trim() !== '') ||
                            (q.ragAssignment && (q.ragAssignment.acceptedChapter || q.ragAssignment.accepted))
                        )
                    } else {
                        chapterOk = !!(
                            (q.chapter != null && String(q.chapter).trim() !== '') ||
                            (q.ragAssignment && (q.ragAssignment.acceptedChapter || q.ragAssignment.accepted))
                        )
                    }
                } catch (e) {
                    chapterOk = false
                }
                const explanationOk = !!(q.explanation && String(q.explanation).trim()) || (q.ragAssignment && (q.ragAssignment.acceptedExplanation || q.ragAssignment.accepted))
                if (!chapterOk || !explanationOk) missing.push({ idx: i, chapterMissing: !chapterOk, explanationMissing: !explanationOk })
            }
            if (missing.length > 0) {
                const msg = missing.map(m => `Q${m.idx + 1}: ${m.chapterMissing ? 'chapter' : ''}${m.chapterMissing && m.explanationMissing ? ' & ' : ''}${m.explanationMissing ? 'explanation' : ''}`).join('\n')
                showToast('Please complete chapter and explanation for the page', 'error')
                alert(`Please complete the following before saving:\n${msg}`)
                return
            }

            const payload: any[] = indices.map(i => {
                const q = localQuestions[i]
                let chapterVal: any = q.chapter
                const resolvedApi = mapToApiChapterId(q.chapter)
                if (resolvedApi != null) {
                    chapterVal = resolvedApi
                } else if (q.ragAssignment && q.ragAssignment.acceptedChapter) {
                    const gemChapter = q.ragAssignment?.geminiSuggestion?.chapter_id
                    const chosen = gemChapter != null ? gemChapter : (q.ragAssignment.apiChapterId ?? q.ragAssignment.textbookChapterId)
                    const resolvedFromRagApi = mapToApiChapterId(chosen)
                    chapterVal = resolvedFromRagApi != null ? resolvedFromRagApi : (chosen != null ? Number(chosen) : q.chapter)
                } else {
                    chapterVal = q.chapter
                }

                let referenceVal = ''
                if (q.reference && String(q.reference).trim()) referenceVal = String(q.reference)
                else if (q.ragAssignment && q.ragAssignment.geminiSuggestion && q.ragAssignment.geminiSuggestion.references) {
                    const refs = q.ragAssignment.geminiSuggestion.references
                    referenceVal = Array.isArray(refs) ? refs.join(' <br> ') : String(refs)
                }

                const body: any = {
                    ques: q.question ?? q.ques ?? '',
                    option1: q.choice_a ?? q.option1 ?? '',
                    option2: q.choice_b ?? q.option2 ?? '',
                    option3: q.choice_c ?? q.option3 ?? '',
                    option4: q.choice_d ?? q.option4 ?? '',
                    correct_ans: (q.corrected_answer ?? q.correctedAnswer ?? q.ans ?? q.answer ?? q.correct_ans ?? ''),
                    explanation: q.explanation ?? '',
                    note: q.note ?? '',
                    chapter: (chapterVal !== null && chapterVal !== undefined && String(chapterVal).trim() !== '') ? Number(chapterVal) : '',
                    reference: referenceVal,
                    corrected_answer: q.corrected_answer ?? q.correctedAnswer ?? '',
                    ...(q.id ? { id: q.id } : {}),
                    original: { ...q }
                }

                if (useChapterAndSubchapter && targetLevel === 'college_university') {
                    const opt: any = { is_chapteroption: true }
                    if (q.subchapter && String(q.subchapter).trim()) {
                        opt.subchapter_option = Number(q.subchapter)
                        const parent = subToParent[String(q.subchapter)]
                        if (parent) opt.chapter_option = Number(parent)
                        else if (q.chapter && String(q.chapter).trim()) opt.chapter_option = Number(q.chapter)
                    } else if (chapterVal !== null && chapterVal !== undefined && String(chapterVal).trim() !== '') {
                        opt.chapter_option = Number(chapterVal)
                    }
                    delete body.chapter
                    Object.assign(body, opt)
                }
                return body
            })

            const { end } = getPageIndices()
            const isLastPage = end >= localQuestions.length
            const saveResp = await onSave(payload, { partial: !isLastPage })
            const anyResp: any = (saveResp as any) || {}
            const savedIds: Array<number | null> = Array.isArray(anyResp?.data?.saved_ids) ? anyResp.data.saved_ids : (Array.isArray(anyResp?.saved_ids) ? anyResp.saved_ids : (Array.isArray(anyResp?.data?.result?.saved_ids) ? anyResp.data.result.saved_ids : []))

            const uploadPromises: Array<Promise<void>> = []
            indices.forEach((origIdx, iOrder) => {
                const q = localQuestions[origIdx]
                if (q && q.pendingImageFile) {
                    const file = q.pendingImageFile as File
                    uploadPromises.push((async () => {
                        try {
                            const form = new FormData()
                            form.append('image', file)
                            const maybeId = (Array.isArray(savedIds) && savedIds.length > iOrder) ? savedIds[iOrder] : (q.id || null)
                            if (maybeId) form.append('question_id', String(maybeId))
                            const targetExamId = String(examId || q.exam_id || '')
                            try {
                                const data = await request(`/api/school/exam-detail/${targetExamId}/upload-question-image/`, { method: 'POST', headers: { Authorization: token ? `Bearer ${token}` : '' }, body: form })
                                const img = data?.image
                                if (img) {
                                    updateQuestionAt(origIdx, { image: img })
                                }
                            } catch (e) {
                                console.warn('Image upload failed for question', origIdx)
                            }
                        } catch (e) {
                            console.warn('Image upload exception', e)
                        }
                    })())
                }
            })
            if (uploadPromises.length > 0) await Promise.all(uploadPromises)

            setLocalQuestions(prev => prev.filter((_, idx) => !indices.includes(idx)))
            showToast('Page saved and cleared from memory', 'success')
        } catch (err) {
            console.error('saveAndNext failed', err)
            showToast('Failed to save page. See console for details.', 'error')
        } finally {
            setIsSaving(false)
        }
    }, [isSaving, localQuestions, onSave, getPageIndices, useChapterAndSubchapter, targetLevel, subToParent, examId, token, showToast, updateQuestionAt, mapToApiChapterId])


    return (
        <div>
            <div className="flex items-center justify-between">
                <h2 className="pb-3 pt-1 text-base font-semibold">Add chapter & explanation</h2>
                <div className="flex items-center gap-3">
                    {hasCompletedRagSuggestions && (
                        <>
                            <Button size="sm" className="bg-primary text-white hover:bg-primary-dark" onClick={() => {
                                const start = page * PAGE_SIZE
                                const end = Math.min(start + PAGE_SIZE, localQuestions.length)
                                const newLocalQuestions = [...localQuestions];
                                for (let i = start; i < end; i++) {
                                    const q = newLocalQuestions[i]
                                    if (!q || !q.ragAssignment) continue;
                                    const gemChapter = q.ragAssignment?.geminiSuggestion?.chapter_id;
                                    const textbookCandidate = q.ragAssignment.textbookChapterId ?? null;
                                    const apiCandidate = q.ragAssignment.apiChapterId ?? null;
                                    const chosen = gemChapter != null ? gemChapter : (apiCandidate != null ? apiCandidate : textbookCandidate)
                                    const apiId = mapToApiChapterId(chosen) || chosen
                                    newLocalQuestions[i] = {
                                        ...newLocalQuestions[i],
                                        ragAssignment: { ...q.ragAssignment, acceptedChapter: true, accepted: true },
                                        chapter: String(apiId)
                                    };
                                }
                                setLocalQuestions(newLocalQuestions);
                                showToast('Page chapters accepted locally', 'success')
                            }}>Accept all chapters</Button>

                            <Button size="sm" className="bg-primary text-white hover:bg-primary-dark" onClick={() => {
                                const start = page * PAGE_SIZE
                                const end = Math.min(start + PAGE_SIZE, localQuestions.length)
                                const newLocalQuestions = [...localQuestions];
                                for (let i = start; i < end; i++) {
                                    const q = newLocalQuestions[i]
                                    if (!q || !q.ragAssignment) continue;
                                    const expl = q.ragAssignment?.geminiSuggestion?.explanation || '';
                                    const corrected = q.ragAssignment?.geminiSuggestion?.corrected_answer;
                                    newLocalQuestions[i] = {
                                        ...newLocalQuestions[i],
                                        ragAssignment: { ...q.ragAssignment, acceptedExplanation: true, accepted: true },
                                        explanation: expl
                                    };
                                    if (corrected) newLocalQuestions[i].corrected_answer = String(corrected);
                                }
                                setLocalQuestions(newLocalQuestions);
                                showToast('Page explanations accepted locally', 'success')
                            }}>Accept all explanations</Button>
                        </>
                    )}
                    <Button color="light" size="sm" onClick={() => { setShowRagModal(true); fetchRags(); }}>
                        Get suggestion
                    </Button>
                    {targetLevel === 'college_university' && (
                        <Button color="light" size="sm" onClick={() => { setSingleChapterIndex(null); setShowChapterModal(true) }} className="ml-2">
                            Get Chapter
                        </Button>
                    )}
                </div>
            </div>
            <p className="text-sm text-bodydark2 mb-4">Add a chapter and optional explanation for each question before saving to the database.</p>
            <div className="space-y-6">
                {(() => {
                    const start = page * PAGE_SIZE
                    const pageSlice = localQuestions.slice(start, start + PAGE_SIZE)
                    return pageSlice.map((q, i) => (
                        <QuestionItem 
                            key={q.id || `local_${start + i}`}
                            index={start + i}
                            colorIndex={i}
                            q={q}
                            targetLevel={targetLevel}
                            runningSingleRag={runningSingleRag}
                            runRagForSingleQuestion={runRagForSingleQuestion}
                            setSingleChapterIndex={setSingleChapterIndex}
                            setShowChapterModal={setShowChapterModal}
                            isRunningChapterAssign={isRunningChapterAssign}
                            chapterAlone={chapterAlone}
                            useChapterAndSubchapter={useChapterAndSubchapter}
                            subToParent={subToParent}
                            updateQuestionAt={updateQuestionAt}
                            setField={setField}
                            combinedChapters={combinedChapters}
                            subchapterCache={subchapterCache}
                            fetchSubchaptersForId={fetchSubchaptersForId}
                            mapToApiChapterId={mapToApiChapterId}
                            mapToSubjectChapterId={mapToSubjectChapterId}
                            token={token}
                            showToast={showToast}
                        />
                    ))
                })()}

                <div className="flex items-center justify-between mt-4">
                    <div>
                        {(() => {
                            const { end } = getPageIndices()
                            const isLastPage = end >= localQuestions.length
                            return (
                                <button className="px-3 py-2 rounded bg-primary text-white" onClick={() => saveAndNext()} disabled={isSaving || (localQuestions.length === 0)}>
                                    {isLastPage ? 'Save and Finish' : 'Save and Next'}
                                </button>
                            )
                        })()}
                    </div>
                    <div className="text-sm text-bodydark2">Page {page + 1} of {Math.max(1, Math.ceil(localQuestions.length / PAGE_SIZE))}</div>
                </div>
            </div>

            <Modal show={showRagModal} size="xl" onClose={() => setShowRagModal(false)}>
                <Modal.Header>
                    <div className="flex items-center justify-between w-full">
                        <div>Select RAG for this exam</div>
                        {isLoadingRags && <div className="ml-3 text-sm text-bodydark2">Loading…</div>}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        {isRunningRag ? (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">Running RAG</div>
                                    <div className="text-sm text-bodydark2">{ragProgress.completed}/{ragProgress.total} completed</div>
                                </div>
                                <ProgressBar completed={ragProgress.completed} total={ragProgress.total} label={`Processing questions`} striped />
                                <div className="text-sm text-bodydark2">Requests left: {Math.max(0, ragProgress.total - ragProgress.completed)}</div>
                            </div>
                        ) : (
                            isLoadingRags ? (
                                <div className="flex items-center gap-2"><Spinner size="sm" /> Loading RAGs…</div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="space-y-4">
                                        {/* Main RAG list (filtered for grade/subject) */}
                                        {rags && rags.length > 0 && (
                                            <div className="grid grid-cols-1 gap-2">
                                                <div className="text-sm font-medium mb-1">RAG books for the current grade/subject</div>
                                                {(() => {
                                                    const selIds = new Set(selectedRags.map(String))
                                                    const selectedObjs: any[] = []
                                                    for (const id of selectedRags) {
                                                        const idStr = String(id)
                                                        let found = (rags || []).find((b: any) => String(b.id) === idStr)
                                                        if (found) selectedObjs.push(found)
                                                    }

                                                    const remaining = (rags || []).filter((b: any) => !selIds.has(String(b.id)))
                                                    const display = [...selectedObjs, ...remaining]
                                                    return display.map((r: any) => (
                                                        <label key={r.id} className={`p-3 border rounded ${selectedRags.includes(String(r.id)) ? 'border-primary bg-primary/5' : ''}`}>
                                                            <input type="checkbox" name="rag" value={r.id} checked={selectedRags.includes(String(r.id))} onChange={(e) => {
                                                                const idStr = String(r.id)
                                                                setSelectedRags(prev => e.target.checked ? [...prev, idStr] : prev.filter(x => x !== idStr))
                                                            }} />
                                                            <span className="ml-2 font-semibold">{r.title || r.name || `Book ${r.id}`}</span>
                                                            <div className="text-sm text-bodydark2">
                                                                {r.author ? <span>Author: {r.author}</span> : null}
                                                                {r.school_subject_id ? <span>{r.author ? ' • ' : ''}Subject ID: {r.school_subject_id}</span> : null}
                                                                {r.school_grade_ids && r.school_grade_ids.length ? <span>{(r.author || r.school_subject_id) ? ' • ' : ''}Grades: {r.school_grade_ids.join(', ')}</span> : null}
                                                            </div>
                                                        </label>
                                                    ))
                                                })()}
                                            </div>
                                        )}

                                        {/* "Other RAGs for the subject" section - always shown if allRags is available */}
                                        {allRags && (subjectId || subjectName) && (() => {
                                            const sid = subjectId ? String(subjectId) : null
                                            const sname = subjectName ? subjectName.toLowerCase().trim() : null
                                            const others = (allRags || []).filter((b: any) => {
                                                const bSubjectId = b.school_subject_id != null ? String(b.school_subject_id) : null
                                                const bSubjectName = b.subject ? String(b.subject).toLowerCase().trim() : null
                                                const match = (sid != null && bSubjectId === sid) || (sname != null && bSubjectName === sname)
                                                // Exclude books already shown in the main list
                                                const alreadyShown = (rags || []).some((rb: any) => String(rb.id) === String(b.id))
                                                return match && !alreadyShown
                                            })
                                            if (!others || others.length === 0) return null
                                            const dispSubjectName = others[0].school_subject_name || others[0].subject || subjectName || `Subject ${sid}`
                                            return (
                                                <div className="mt-4">
                                                    <div className="text-sm font-medium">Other RAGs for the subject "{dispSubjectName}"</div>
                                                    <div className="grid grid-cols-1 gap-2 mt-2">
                                                        {others.map((o: any) => {
                                                            const idStr = String(o.id)
                                                            return (
                                                                <label key={o.id} className={`p-3 border rounded ${selectedRags.includes(idStr) ? 'border-primary bg-primary/5' : ''}`}>
                                                                    <input type="checkbox" value={o.id} checked={selectedRags.includes(idStr)} onChange={(e) => {
                                                                        setSelectedRags(prev => e.target.checked ? [...prev, idStr] : prev.filter(x => x !== idStr))
                                                                    }} />
                                                                    <span className="ml-2 font-semibold">{o.title || o.name || `Book ${o.id}`}</span>
                                                                    <div className="text-sm text-bodydark2 ml-6">
                                                                        {o.grade ? <span>Grade: {o.grade}</span> : null}
                                                                        {o.author ? <span> • Author: {o.author}</span> : null}
                                                                    </div>
                                                                </label>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )
                                        })()}

                                        {/* Fallback if no books are found in either section */}
                                        {(!rags || rags.length === 0) && (!allRags || !allRags.some((b: any) => {
                                            const sid = subjectId ? String(subjectId) : null
                                            const sname = subjectName ? subjectName.toLowerCase().trim() : null
                                            const bSubjectId = b.school_subject_id != null ? String(b.school_subject_id) : null
                                            const bSubjectName = b.subject ? String(b.subject).toLowerCase().trim() : null
                                            return (sid != null && bSubjectId === sid) || (sname != null && bSubjectName === sname)
                                        })) && (
                                            <div className="text-sm text-bodydark2">No RAG indexes available for the selected subject.</div>
                                        )}
                                    </div>
                                        <div className="mt-3 flex items-center gap-3">
                                            <label className="text-sm text-bodydark2">Gemini batch:</label>
                                            <input aria-label="Gemini batch size" type="number" min={1} max={10} value={geminiBatchSize} onChange={(e) => setGeminiBatchSize(Number(e.target.value || 1))} className="w-20 rounded border p-1 text-sm" />
                                            <div className="text-sm text-bodydark2">(questions per Gemini call)</div>
                                        </div>
                                        
                                            {targetLevel === 'college_university' && (
                                                <div className="mt-2 flex items-center gap-3">
                                                    <label className="flex items-center gap-2 text-sm">
                                                        <input type="checkbox" checked={chapterAlone} onChange={(e) => setChapterAlone(e.target.checked)} />
                                                        <span>Chapter alone (do not ask AI to pick a chapter)</span>
                                                    </label>
                                                    <div className="text-sm text-bodydark2">When enabled Gemini will only generate explanation, references and corrected answer (no chapter suggestion)</div>
                                                </div>
                                            )}
                                        
                                </div>
                            )
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3">
                        <Button color="gray" onClick={() => setShowRagModal(false)} disabled={isRunningRag}>Cancel</Button>
                        {!isRunningRag ? (
                            <Button onClick={runRagForQuestions} disabled={selectedRags.length === 0 || isRunningRag}>
                                Continue
                            </Button>
                        ) : (
                            <Button color="light" disabled>
                                Running…
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>
            <Modal show={showChapterModal} size="lg" onClose={() => setShowChapterModal(false)}>
                <Modal.Header>
                    Assign Chapter / Subchapter
                </Modal.Header>
                <Modal.Body>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2">
                                <input aria-label="use-chapter-subchapter" title="use chapter and subchapter" type="checkbox" checked={useChapterAndSubchapter} onChange={(e) => setUseChapterAndSubchapter(e.target.checked)} />
                                <span>Use Chapter and Subchapter option</span>
                            </label>
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="text-sm">Questions per batch</label>
                            <input aria-label="questions-per-batch" title="Questions per batch" type="number" min={1} max={50} value={chapterBatchSize} onChange={(e) => setChapterBatchSize(Number(e.target.value || 5))} className="w-24 rounded border p-1 text-sm" />
                            <div className="text-sm text-bodydark2">(How many questions to send to Gemini per call)</div>
                        </div>
                        <div className="text-sm text-bodydark2">This will fetch chapters/subchapters for the selected courses and ask Gemini to assign the best chapter/subchapter to each question. Results are saved locally in the question's RAG assignment.</div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3">
                        <Button color="gray" onClick={() => { setShowChapterModal(false); setSingleChapterIndex(null); }} disabled={isRunningChapterAssign}>Cancel</Button>
                        <Button onClick={runChapterAssign} disabled={isRunningChapterAssign}>
                            {isRunningChapterAssign ? 'Running…' : 'Generate'}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
            {toast ? (
                <div className={`fixed right-4 bottom-6 z-50 p-3 rounded shadow ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                    <div className="text-sm">{toast.msg}</div>
                </div>
            ) : null}
        </div>
    )
}

export default ExamQuestionsMeta
